import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useToast, ToastContainer, WidgetSkeleton, LineSkeleton, MeetingListSkeleton, ConfirmDialog, BillStatusBadge } from "./toast";
import MI_COMMITTEES from "./miCommittees";

const SERVER = "https://legisim-server.onrender.com";
const PROXY  = "https://legisim-server.onrender.com";


function displayStatus(s) {
  const MAP = {
    "Introduced":"Introduced","Engrossed":"In Progress","Enrolled":"PA Signed",
    "Chaptered":"PA Signed","Passed":"Passed","Failed":"Failed","Vetoed":"Vetoed",
    "Report Pass":"In Committee","Report Fail":"In Committee","Refer":"In Committee",
    "Override":"Passed","Draft":"Draft",
  };
  return MAP[s] || s || "Introduced";
}
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700;1,800&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}

.dash-layout{display:flex;height:100vh;overflow:hidden;background:var(--bg);}

/* ── SIDEBAR ── */
.dash-sidebar{
  width:var(--sidebar-width);flex-shrink:0;background:#111118;
  border-right:1px solid rgba(255,255,255,0.07);display:flex;flex-direction:column;
  overflow:hidden;position:relative;z-index:10;
}
[data-theme="light"] .dash-sidebar{background:#1a2744;border-right-color:rgba(255,255,255,0.08);}
[data-theme="light"] .sidebar-nav{background:#1a2744;}
[data-theme="light"] .sidebar-footer{background:#1a2744;border-top-color:rgba(255,255,255,0.08);}
[data-theme="light"] .sidebar-brand{border-bottom-color:rgba(255,255,255,0.08);}
.dash-sidebar::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#c9a84c,transparent);}
.sidebar-brand{padding:1.1rem 1.25rem 0.9rem;border-bottom:1px solid rgba(255,255,255,0.07);flex-shrink:0;}
.sidebar-wordmark{font-family:var(--font-display);font-size:1.3rem;font-weight:900;letter-spacing:-0.03em;color:#e6e4dc;line-height:1;}
.sidebar-wordmark em{font-style:italic;color:#c9a84c;}
.sidebar-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:#6a6860;vertical-align:super;}
.sidebar-sub{font-family:var(--font-mono);font-size:0.54rem;color:#6a6860;letter-spacing:0.1em;text-transform:uppercase;margin-top:0.3rem;}

.sidebar-nav{flex:1;padding:0.6rem 0.65rem;display:flex;flex-direction:column;gap:3px;overflow:hidden;background:#111118;}
.sidebar-btn{
  display:flex;align-items:center;padding:0.85rem 0.9rem;
  background:transparent;border:none;border-radius:8px;
  font-family:var(--font-mono);font-size:0.75rem;
  color:#9a9890;cursor:pointer;transition:all 0.15s;text-align:left;
  width:100%;letter-spacing:0.05em;white-space:nowrap;
}
.sidebar-btn:hover{background:rgba(201,168,76,0.08);color:var(--gold);border-color:rgba(201,168,76,0.2);}
.sidebar-btn.active{background:rgba(201,168,76,0.15);color:var(--gold);font-weight:600;border:1px solid rgba(201,168,76,0.25);}
.sidebar-sep{height:1px;background:rgba(255,255,255,0.06);margin:3px 2px;}
.sidebar-footer{padding:0.65rem;border-top:1px solid rgba(255,255,255,0.07);background:#111118;display:flex;flex-direction:column;gap:3px;flex-shrink:0;}
.sidebar-foot-btn{background:transparent;border:1px solid rgba(255,255,255,0.07);border-radius:8px;padding:0.7rem 0.9rem;font-family:var(--font-mono);font-size:0.75rem;color:#9a9890;cursor:pointer;transition:all 0.15s;text-align:left;letter-spacing:0.06em;width:100%;}
.sidebar-foot-btn:hover{border-color:rgba(201,168,76,0.3);color:var(--gold);background:rgba(201,168,76,0.05);}
.sidebar-foot-btn.signout:hover{border-color:rgba(192,57,43,0.4);color:var(--red);}

/* ── MAIN ── */
.dash-main{flex:1;overflow:hidden;display:flex;flex-direction:column;}
.dash-topbar{display:flex;justify-content:flex-end;align-items:center;padding:0.75rem 2rem;border-bottom:1px solid var(--border);flex-shrink:0;background:var(--surface);}
.theme-toggle{display:flex;align-items:center;gap:0.5rem;background:var(--surface2);border:1px solid var(--border);border-radius:20px;padding:0.35rem 0.85rem;cursor:pointer;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);transition:all 0.18s;letter-spacing:0.05em;}
.theme-toggle:hover{border-color:var(--gold-dim);color:var(--text);}
.theme-toggle-icon{font-size:0.85rem;}
.dash-content{padding:1.75rem 2rem;flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.dash-greeting{font-family:var(--font-display);font-size:1.6rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);margin-bottom:0.2rem;}
[data-theme="light"] .dash-greeting-name{color:var(--gold);font-style:italic;}
.dash-session{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.1em;margin-bottom:1.5rem;}

/* ── BANNER ── */
.dash-banner{display:grid;grid-template-columns:repeat(4,1fr);gap:1.25rem;margin-bottom:1.5rem;}
.onboarding-banner{display:flex;align-items:flex-start;justify-content:space-between;gap:1.5rem;background:linear-gradient(135deg,rgba(201,168,76,0.08) 0%,rgba(201,168,76,0.04) 100%);border:1px solid rgba(201,168,76,0.25);border-radius:12px;padding:1.1rem 1.25rem;margin-bottom:1.5rem;}
.onboarding-steps{display:flex;gap:1.5rem;flex-wrap:wrap;}
.onboarding-step{display:flex;align-items:center;gap:0.6rem;font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);}
.onboarding-step-num{width:20px;height:20px;border-radius:50%;background:rgba(201,168,76,0.15);border:1px solid rgba(201,168,76,0.3);color:var(--gold);font-size:0.6rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.onboarding-step button{background:none;border:none;padding:0;cursor:pointer;color:var(--gold);font-family:var(--font-mono);font-size:0.7rem;font-weight:600;text-decoration:underline;text-underline-offset:2px;}
.onboarding-step button:hover{opacity:0.8;}
.onboarding-dismiss{background:none;border:none;cursor:pointer;color:var(--muted);font-size:0.8rem;opacity:0.5;padding:0.15rem 0.3rem;border-radius:4px;transition:opacity 0.15s;flex-shrink:0;}
.onboarding-dismiss:hover{opacity:1;}
[data-theme="light"] .onboarding-banner{background:linear-gradient(135deg,rgba(160,120,48,0.06) 0%,rgba(160,120,48,0.02) 100%);border-color:rgba(160,120,48,0.2);}
.banner-stat{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:1.6rem 1.5rem 1.25rem;transition:border-color 0.18s;text-align:center;display:flex;flex-direction:column;min-height:200px;}
.banner-stat.clickable{cursor:pointer;}
.banner-stat.clickable:hover{border-color:var(--gold-dim);}
.banner-label{font-family:var(--font-mono);font-size:0.85rem;color:var(--text);letter-spacing:0.06em;text-transform:uppercase;margin-bottom:0.5rem;}
.banner-value{font-family:var(--font-display);font-size:2.8rem;font-weight:800;color:var(--gold);line-height:1;}
/* Light mode handled by public/theme.css */
.banner-sub{font-family:var(--font-mono);font-size:0.78rem;color:var(--muted);margin-top:0.4rem;}
.banner-btn{display:block;width:100%;margin-top:auto;padding:0.55rem 1rem;background:rgba(201,168,76,0.06);border:1px solid var(--gold-dim);border-radius:8px;font-family:var(--font-mono);font-size:0.75rem;color:var(--gold);cursor:pointer;transition:all 0.18s;letter-spacing:0.05em;text-align:center;}
.banner-btn:hover{background:var(--gold-glow);border-color:var(--gold);}
.pulse-widget{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.3rem;cursor:default;padding:0.5rem;}
.pulse-score{font-family:var(--font-display);font-size:2.8rem;font-weight:900;line-height:1;transition:color 0.4s;}
.pulse-label{font-family:var(--font-mono);font-size:0.75rem;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;transition:color 0.4s;}
.pulse-sub{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);margin-top:0.15rem;}
.pulse-stats{display:flex;gap:0.75rem;margin-top:0.4rem;font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);}
.pulse-btn{display:block;width:100%;margin-top:auto;background:transparent;border:1px solid;border-radius:8px;padding:0.55rem 1rem;font-family:var(--font-mono);font-size:0.75rem;cursor:pointer;transition:all 0.18s;letter-spacing:0.05em;text-align:center;}
@keyframes pulse-beat{0%,100%{transform:scale(1);}50%{transform:scale(1.08);}}
@keyframes pulse-fast{0%,100%{transform:scale(1);}50%{transform:scale(1.12);}}
@keyframes pulse-racing{0%,100%{transform:scale(1);}40%{transform:scale(1.18);}60%{transform:scale(0.95);}80%{transform:scale(1.1);}}
@keyframes pulse-code-red{0%{transform:scale(1);}10%{transform:scale(1.25);}20%{transform:scale(0.92);}30%{transform:scale(1.2);}40%{transform:scale(0.94);}50%{transform:scale(1.15);}100%{transform:scale(1);}}

/* ── WIDGETS ── */
.dash-widgets{display:grid;grid-template-columns:1fr 1fr 1fr;gap:1.25rem;align-items:start;}
.widget{background:var(--surface);border:1px solid var(--border);border-radius:14px;display:flex;flex-direction:column;overflow:hidden;height:460px;}
.widget-header{padding:1.1rem 1.4rem 0.85rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.widget-title{font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;}
.widget-action{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.3rem 0.8rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);cursor:pointer;transition:all 0.18s;letter-spacing:0.05em;}
.widget-action:hover{border-color:var(--gold-dim);color:var(--gold);}
.widget-body{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;padding:0.5rem 0;}
.widget-empty{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);text-align:center;padding:2rem 1rem;font-style:italic;}
.loading-row{display:flex;align-items:center;justify-content:center;padding:1.5rem;gap:0.5rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);}
.spin{width:14px;height:14px;border:2px solid var(--border);border-top-color:var(--gold);border-radius:50%;animation:spin 0.8s linear infinite;}
@keyframes spin{to{transform:rotate(360deg)}}

/* ── MEETING ITEMS ── */
.meeting-item{padding:0.8rem 1.4rem;border-bottom:1px solid rgba(255,255,255,0.03);cursor:pointer;transition:background 0.15s;}
.meeting-item:last-child{border-bottom:none;}
.meeting-item:hover{background:var(--surface2);}
.meeting-name{font-size:0.9rem;color:var(--text);font-weight:500;line-height:1.3;transition:color 0.15s;margin-bottom:0.2rem;}
.meeting-item:hover .meeting-name{color:var(--gold);}
.meeting-meta{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);}
.pip{display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:0.3rem;vertical-align:middle;}
.pip.House{background:var(--house);}
.pip.Senate{background:var(--senate);}

/* ── BILL ITEMS ── */
.bill-item{padding:0.8rem 1.4rem;border-bottom:1px solid rgba(255,255,255,0.03);transition:all 0.15s;}
.bill-item:last-child{border-bottom:none;}
.bill-item:hover{background:var(--surface2);}
.bill-item.moved{border-left:2px solid var(--gold);padding-left:calc(1.25rem - 2px);cursor:pointer;}
.bill-item.moved:hover{border-left-color:transparent;}
.bill-number{font-family:var(--font-mono);font-size:0.68rem;color:var(--gold);margin-bottom:0.15rem;}
.bill-title{font-size:0.88rem;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:0.15rem;}
.bill-meta{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);}

/* ── COMMITTEE ITEMS ── */
.committee-item{padding:0.85rem 1.4rem;border-bottom:1px solid rgba(255,255,255,0.03);}
.committee-item:last-child{border-bottom:none;}
.committee-name{font-size:0.92rem;color:var(--text);font-weight:500;margin-bottom:0.3rem;}
.committee-badges{display:flex;align-items:center;gap:0.4rem;flex-wrap:wrap;margin-bottom:0.3rem;}
.badge{font-family:var(--font-mono);font-size:0.56rem;padding:0.1rem 0.4rem;border-radius:4px;}
.badge.House{background:var(--house-bg);color:var(--house);border:1px solid rgba(59,111,212,0.2);}
.badge.Senate{background:var(--senate-bg);color:var(--senate);border:1px solid rgba(155,77,202,0.2);}
.badge.type{background:var(--charcoal);color:var(--muted);border:1px solid var(--border);}
.committee-actions{display:flex;gap:0.4rem;}
.btn-info{background:var(--gold-glow);border:1px solid var(--gold-dim);border-radius:5px;padding:0.2rem 0.55rem;font-family:var(--font-mono);font-size:0.58rem;color:var(--gold);cursor:pointer;transition:all 0.18s;}
.btn-info:hover{background:rgba(201,168,76,0.2);}
.btn-unfollow{background:transparent;border:1px solid var(--border);border-radius:5px;padding:0.2rem 0.55rem;font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);cursor:pointer;transition:all 0.18s;}
.btn-unfollow:hover{border-color:rgba(192,57,43,0.4);color:var(--red);}

/* ── MODALS ── */
.overlay{position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:200;display:flex;align-items:center;justify-content:center;padding:2rem;}
.modal{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;padding:2rem;max-width:560px;width:100%;max-height:80vh;display:flex;flex-direction:column;gap:1rem;}
.modal-title{font-family:var(--font-display);font-size:1.25rem;font-weight:800;color:var(--text);}
.modal-sub{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);}
.modal-input{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.7rem 1rem;font-family:var(--font-body);font-size:0.9rem;color:var(--text);outline:none;transition:border-color 0.18s;width:100%;}
.modal-input:focus{border-color:rgba(201,168,76,0.4);}
.modal-input::placeholder{color:var(--muted);}
.modal-filters{display:flex;gap:0.4rem;flex-wrap:wrap;}
.filter-chip{background:var(--surface2);border:1px solid var(--border);border-radius:100px;padding:0.22rem 0.65rem;font-size:0.62rem;font-family:var(--font-mono);color:var(--muted);cursor:pointer;transition:all 0.15s;}
.filter-chip:hover{border-color:var(--gold-dim);color:var(--text);}
.filter-chip.active{background:var(--gold-glow);border-color:var(--gold-dim);color:var(--gold);}
.modal-results{flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:0.5rem;scrollbar-width:thin;}
.result-item{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:0.75rem 1rem;background:var(--surface);border:1px solid var(--border);border-radius:8px;}
.result-name{font-size:0.85rem;color:var(--text);font-weight:500;flex:1;}
.result-meta{display:flex;gap:0.4rem;align-items:center;flex-shrink:0;}
.btn-follow{background:var(--gold-glow);border:1px solid var(--gold-dim);border-radius:6px;padding:0.28rem 0.7rem;font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);cursor:pointer;transition:all 0.18s;}
.btn-follow:hover{background:rgba(201,168,76,0.2);}
.btn-follow.on{background:rgba(46,125,82,0.12);border-color:rgba(46,125,82,0.3);color:#4ade80;}
.btn-follow.on:hover{background:rgba(192,57,43,0.1);border-color:rgba(192,57,43,0.3);color:var(--red);}
.btn-close{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.5rem 1.25rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);cursor:pointer;transition:all 0.18s;align-self:flex-end;}
.btn-close:hover{border-color:var(--gold-dim);color:var(--gold);}

/* Info modal */
.info-modal{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;padding:0;max-width:600px;width:100%;max-height:85vh;display:flex;flex-direction:column;overflow:hidden;}
.info-head{padding:1.75rem 2rem 1.25rem;border-bottom:1px solid var(--border);position:relative;}
.info-head::before{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);}
.info-chamber{font-family:var(--font-mono);font-size:0.6rem;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:0.4rem;}
.info-chamber.House{color:var(--house);}
.info-chamber.Senate{color:var(--senate);}
.info-name{font-family:var(--font-display);font-size:1.4rem;font-weight:800;color:var(--text);line-height:1.2;margin-bottom:0.75rem;}
.info-body{flex:1;overflow-y:auto;padding:1.5rem 2rem;display:flex;flex-direction:column;gap:1.25rem;scrollbar-width:thin;}
.info-section{font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);letter-spacing:0.12em;text-transform:uppercase;margin-bottom:0.6rem;}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:0.6rem;}
.info-cell{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.6rem 0.85rem;}
.info-cell-label{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;margin-bottom:0.2rem;}
.info-cell-value{font-size:0.82rem;color:var(--text);font-weight:500;}
.info-member{display:flex;align-items:center;justify-content:space-between;padding:0.35rem 0;border-bottom:1px solid rgba(255,255,255,0.03);}
.info-member:last-child{border-bottom:none;}
.info-member-name{font-size:0.82rem;color:var(--text);}
.info-member-role{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);}
.info-member-role.chair{color:var(--gold);}
.info-agenda{padding:0.5rem 0;border-bottom:1px solid rgba(255,255,255,0.03);font-size:0.82rem;color:var(--text);line-height:1.4;}
.info-agenda:last-child{border-bottom:none;}
.info-agenda-num{font-family:var(--font-mono);font-size:0.65rem;color:var(--gold);margin-bottom:0.15rem;}
.info-no-meeting{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:1rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);text-align:center;}
.info-foot{padding:1rem 2rem;border-top:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-wrap:wrap;}
.btn-ics{display:inline-flex;align-items:center;gap:0.4rem;background:var(--gold);border:none;border-radius:8px;padding:0.5rem 1rem;font-family:var(--font-mono);font-size:0.65rem;font-weight:600;color:#09090e;cursor:pointer;transition:filter 0.18s;}
.btn-ics:hover{filter:brightness(1.1);}
.btn-leg{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.5rem 1rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);cursor:pointer;transition:all 0.18s;text-decoration:none;}
.btn-leg:hover{border-color:var(--gold-dim);color:var(--gold);}

/* Weekly bills modal */
.weekly-modal{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;padding:0;max-width:620px;width:100%;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;}
.weekly-head{padding:1.5rem 2rem 1rem;border-bottom:1px solid var(--border);}
.weekly-title{font-family:var(--font-display);font-size:1.25rem;font-weight:800;color:var(--text);margin-bottom:0.25rem;}
.weekly-body{flex:1;overflow-y:auto;padding:0.5rem 2rem;scrollbar-width:thin;}
.weekly-bill{padding:0.75rem 0;border-bottom:1px solid rgba(255,255,255,0.04);}
.weekly-bill:last-child{border-bottom:none;}
.weekly-number{font-family:var(--font-mono);font-size:0.62rem;color:var(--gold);margin-bottom:0.15rem;}
.weekly-title-text{font-size:0.85rem;color:var(--text);line-height:1.4;margin-bottom:0.15rem;}
.weekly-meta{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);}
.weekly-foot{padding:1rem 2rem;border-top:1px solid var(--border);display:flex;justify-content:flex-end;}


/* ── NEWS WIDGET ── */
.news-widget{background:var(--surface);border:1px solid var(--border);border-radius:14px;overflow:hidden;margin-top:1.25rem;}
.news-widget-header{padding:0.85rem 1.5rem 0.75rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;background:#1a2744;}
.news-widget-title{font-family:var(--font-display);font-size:1.25rem;font-weight:800;color:#e6e4dc;letter-spacing:-0.01em;}
.news-widget-meta{font-family:var(--font-mono);font-size:0.68rem;color:rgba(255,255,255,0.5);letter-spacing:0.04em;}
.news-cols{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid var(--border);height:520px;}
.news-col{display:flex;flex-direction:column;overflow:hidden;min-height:0;}
.news-col:first-child{border-right:1px solid var(--border);}
.col-hdr{padding:0.65rem 1.25rem;border-bottom:1px solid var(--border);display:flex;align-items:baseline;gap:0.5rem;flex-shrink:0;}
.col-hdr-title{font-family:var(--font-display);font-size:1.3rem;font-weight:800;letter-spacing:-0.01em;}
.col-hdr-title.michigan{color:var(--gold);}
.col-hdr-title.national{color:#6b9eff;}
.col-hdr-sub{font-family:var(--font-mono);font-size:0.85rem;color:var(--gold);font-weight:600;}
.story-list{overflow-y:auto;flex:1;min-height:0;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.story{padding:0.85rem 1.25rem;border-bottom:1px solid rgba(255,255,255,0.04);transition:background 0.15s;}
.story:last-child{border-bottom:none;}
.story:hover{background:var(--surface2);}
.story-link{font-size:0.85rem;color:var(--text);font-weight:500;line-height:1.4;text-decoration:none;display:block;margin-bottom:0.3rem;transition:color 0.15s;}
.news-col:first-child .story:hover .story-link{color:var(--gold);}
.news-col:last-child .story:hover .story-link{color:#6b9eff;}
.story-excerpt{font-size:0.78rem;color:var(--muted);line-height:1.4;margin-bottom:0.4rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.story-footer{display:flex;align-items:center;justify-content:space-between;gap:0.5rem;flex-wrap:wrap;}
.story-tags{display:flex;gap:0.3rem;flex-wrap:wrap;}
.story-tag{font-family:var(--font-mono);font-size:0.55rem;padding:0.1rem 0.35rem;border-radius:4px;border:1px solid;}
.story-byline{display:flex;align-items:center;gap:0.35rem;flex-shrink:0;}
.story-source{font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);}
.story-time{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);}
.story-sep{width:3px;height:3px;border-radius:50%;background:var(--muted);}
.news-loading{display:flex;align-items:center;justify-content:center;padding:2rem;gap:0.5rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);}
@media(max-width:900px){.news-cols{grid-template-columns:1fr;max-height:none;} .news-col:first-child{border-right:none;border-bottom:1px solid var(--border);}}


/* ── BILL DETAIL MODAL ── */
.bill-detail-modal{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;padding:0;max-width:640px;width:100%;max-height:85vh;display:flex;flex-direction:column;overflow:hidden;}
.bill-detail-head{padding:1.75rem 2rem 1.25rem;border-bottom:1px solid var(--border);position:relative;}
.bill-detail-head::before{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);}
.bill-detail-number{font-family:var(--font-mono);font-size:0.75rem;color:var(--gold);letter-spacing:0.1em;margin-bottom:0.4rem;}
.bill-detail-title{font-family:var(--font-display);font-size:1.3rem;font-weight:800;color:var(--text);line-height:1.3;margin-bottom:0.75rem;}
.bill-detail-badges{display:flex;gap:0.5rem;flex-wrap:wrap;}
.bill-detail-body{flex:1;overflow-y:auto;padding:1.5rem 2rem;display:flex;flex-direction:column;gap:1rem;scrollbar-width:thin;}
.bill-detail-row{display:flex;flex-direction:column;gap:0.2rem;}
.bill-detail-label{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;}
.bill-detail-value{font-size:0.88rem;color:var(--text);line-height:1.5;}
.bill-detail-foot{padding:1rem 2rem;border-top:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-shrink:0;}
.bill-detail-status{display:inline-block;font-family:var(--font-mono);font-size:0.68rem;padding:0.3rem 0.75rem;border-radius:6px;}

@media(max-width:1100px){.dash-widgets{grid-template-columns:1fr 1fr;} .dash-banner{grid-template-columns:1fr 1fr;}}
@media(max-width:700px){.dash-widgets{grid-template-columns:1fr;} :root{--sidebar-width:180px;} .dash-content{padding:1rem;}}

[data-theme="light"] .dash-content{background:#eef1f7;}
[data-theme="light"] .dash-topbar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .theme-toggle{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .banner-stat{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 1px 3px rgba(26,39,68,0.08);}
[data-theme="light"] .banner-value{color:#1a2744;}
[data-theme="light"] .banner-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .banner-btn:hover{background:rgba(160,120,48,0.14);border-color:#a07830;}
[data-theme="light"] .widget{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 1px 3px rgba(26,39,68,0.08);}
[data-theme="light"] .widget-header{background:#1a2744;border-color:rgba(255,255,255,0.1);}
[data-theme="light"] .widget-title{color:#e6e4dc;}
[data-theme="light"] .widget-action{color:rgba(255,255,255,0.55);border-color:rgba(255,255,255,0.15);}
[data-theme="light"] .bill-detail-foot .widget-action{color:#1a2744;border-color:rgba(26,39,68,0.2);background:transparent;}
[data-theme="light"] .bill-detail-foot .widget-action:hover{color:#c9a84c;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .widget-action:hover{color:#c9a84c;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .news-widget{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-widget-header{background:#1a2744;border-color:rgba(255,255,255,0.1);}
[data-theme="light"] .news-widget-title{color:#e6e4dc;}
[data-theme="light"] .news-widget-meta{color:rgba(255,255,255,0.4);}
[data-theme="light"] .news-col{background:#f8f9fc;}
[data-theme="light"] .col-hdr{background:rgba(26,39,68,0.05);}
[data-theme="light"] .col-hdr-title.michigan,[data-theme="light"] .col-hdr-title.national{color:#1a2744;}
[data-theme="light"] .bill-number{color:#1a2744;}
[data-theme="light"] .bill-meta{color:#6b7280;}
[data-theme="light"] .meeting-name{color:#1a1e2e;}
[data-theme="light"] .meeting-meta{color:#6b7280;}
[data-theme="light"] .committee-name{color:#1a1e2e;}
[data-theme="light"] .dash-greeting{color:#1a1e2e;}
[data-theme="light"] .dash-session{color:#6b7280;}

/* ── DASHBOARD FOOTER ─────────────────────────────────────────────────── */
.dash-footer{margin-top:3rem;border-top:1px solid var(--border);padding:2.5rem 0 1.5rem;}
.dash-footer-grid{display:grid;grid-template-columns:1fr 1fr;gap:2rem;margin-bottom:2rem;}
.dash-footer-col-label{font-size:0.65rem;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:var(--gold);margin-bottom:0.75rem;}
.dash-footer-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;color:var(--text);letter-spacing:-0.01em;margin-bottom:0.3rem;}
.dash-footer-wordmark em{font-style:italic;color:var(--gold);}
.dash-footer-wordmark sup{font-family:var(--font-body);font-size:0.45rem;color:var(--text2);font-weight:400;vertical-align:super;margin-left:1px;}
.dash-footer-tagline{font-size:0.72rem;color:var(--text2);line-height:1.5;max-width:260px;}
.dash-footer-contact-line{display:flex;align-items:center;gap:0.5rem;font-size:0.78rem;color:var(--text2);margin-bottom:0.4rem;}
.dash-footer-contact-line a{color:var(--text2);text-decoration:none;}
.dash-footer-contact-line a:hover{color:var(--gold);}
.dash-footer-contact-icon{font-size:0.8rem;opacity:0.6;flex-shrink:0;}
.dash-footer-hours{margin-top:0.6rem;font-size:0.72rem;color:var(--text2);opacity:0.7;line-height:1.6;}
.dash-footer-bottom{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:0.75rem;padding-top:1.25rem;border-top:1px solid var(--border);}
.dash-footer-copy{font-size:0.68rem;color:var(--text2);opacity:0.6;}
.dash-footer-links{display:flex;flex-wrap:wrap;gap:0.25rem 0.75rem;align-items:center;}
.dash-footer-link{background:none;border:none;padding:0;cursor:pointer;font-size:0.68rem;color:var(--text2);opacity:0.65;text-decoration:none;transition:opacity 0.15s,color 0.15s;}
.dash-footer-link:hover{opacity:1;color:var(--gold);}
.dash-footer-sep{font-size:0.68rem;color:var(--border);opacity:0.6;user-select:none;}
.dash-footer-disclaimer{margin-top:0.75rem;font-size:0.62rem;color:var(--text2);opacity:0.45;line-height:1.5;}
@media(max-width:700px){.dash-footer-grid{grid-template-columns:1fr;} .dash-footer-bottom{flex-direction:column;align-items:flex-start;}}

/* ── PULSE BAR ── */
.pulse-bar{display:flex;align-items:center;gap:1rem;padding:0 1.5rem;height:44px;flex-shrink:0;background:var(--surface);border-bottom:1px solid var(--border);position:relative;overflow:hidden;}
.pulse-bar-ekg{flex:1;height:100%;position:relative;overflow:hidden;cursor:default;}
.pulse-bar-ekg svg{position:absolute;top:0;left:0;width:100%;height:100%;}
.pulse-bar-score{display:flex;align-items:center;gap:0.45rem;flex-shrink:0;}
.pulse-bar-num{font-family:var(--font-display);font-size:1.15rem;font-weight:900;line-height:1;transition:color 0.4s;}
.pulse-bar-tier{font-family:var(--font-mono);font-size:0.6rem;font-weight:600;letter-spacing:0.1em;text-transform:uppercase;transition:color 0.4s;}
.pulse-bar-divider{width:1px;height:20px;background:var(--border);flex-shrink:0;}
.pulse-bar-btn{background:transparent;border:1px solid;border-radius:6px;padding:0.25rem 0.7rem;font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;flex-shrink:0;}
.pulse-bar-btn:hover{opacity:0.8;}
.pulse-bar-toggle{display:flex;align-items:center;gap:0.4rem;background:var(--surface2);border:1px solid var(--border);border-radius:16px;padding:0.25rem 0.7rem;cursor:pointer;font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);transition:all 0.18s;flex-shrink:0;}
.pulse-bar-toggle:hover{border-color:var(--gold-dim);color:var(--text);}
@media(max-width:700px){.pulse-bar-ekg,.pulse-bar-btn,.pulse-bar-divider{display:none;}}
[data-theme="light"] .pulse-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}

/* ── TODAY VIEW ── */
.today-view{display:grid;grid-template-columns:1fr 1fr 1fr;gap:0;border-bottom:1px solid var(--border);height:340px;flex-shrink:0;}
.today-col{display:flex;flex-direction:column;overflow:hidden;border-right:1px solid var(--border);}
.today-col:last-child{border-right:none;}
.today-col-head{padding:0.75rem 1.1rem 0.65rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.today-col-title{font-family:var(--font-display);font-size:0.95rem;font-weight:800;color:var(--text);letter-spacing:-0.01em;line-height:1;}
.today-col-action{font-family:var(--font-mono);font-size:0.62rem;color:var(--gold);cursor:pointer;border:1px solid var(--gold-dim);background:rgba(201,168,76,0.06);padding:0.2rem 0.6rem;border-radius:5px;letter-spacing:0.04em;transition:all 0.15s;font-weight:500;}
.today-col-action:hover{opacity:0.7;}
.today-col-body{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.today-toggle{display:flex;gap:0;flex-shrink:0;}
.today-toggle-btn{background:transparent;border:none;padding:0.18rem 0.55rem;font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);cursor:pointer;border-radius:4px;transition:all 0.15s;}
.today-toggle-btn.active{background:rgba(201,168,76,0.1);color:var(--gold);}
/* Event rows */
.today-event{padding:0.55rem 1.1rem;border-bottom:1px solid rgba(255,255,255,0.03);display:flex;align-items:flex-start;gap:0.6rem;}
.today-event:last-child{border-bottom:none;}
.today-event-pip{width:6px;height:6px;border-radius:50%;flex-shrink:0;margin-top:0.3rem;}
.today-event-info{flex:1;min-width:0;}
.today-event-name{font-size:0.82rem;color:var(--text);line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.today-event-meta{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);margin-top:0.15rem;letter-spacing:0.03em;}
.today-event-actions{display:flex;gap:0.3rem;flex-shrink:0;align-items:flex-start;margin-top:1px;}
.today-event-btn{background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.15);border-radius:5px;padding:0.22rem 0.55rem;font-family:var(--font-mono);font-size:0.58rem;color:rgba(255,255,255,0.7);cursor:pointer;transition:all 0.15s;white-space:nowrap;font-weight:500;}
.today-event-btn:hover{background:rgba(201,168,76,0.12);border-color:var(--gold-dim);color:var(--gold);}
.today-event-btn.unfollow:hover{background:rgba(192,57,43,0.08);border-color:rgba(192,57,43,0.4);color:var(--red);}
[data-theme="light"] .today-event-btn{background:rgba(26,39,68,0.07);border-color:rgba(26,39,68,0.2);color:#4a5568;}
[data-theme="light"] .today-event-btn:hover{background:rgba(160,120,48,0.1);border-color:rgba(160,120,48,0.4);color:#a07830;}
/* Follow-up rows */
.today-followup{padding:0.55rem 1.1rem;border-bottom:1px solid rgba(255,255,255,0.03);}
.today-followup:last-child{border-bottom:none;}
.today-followup-task{font-family:var(--font-body);font-size:0.85rem;color:var(--text);line-height:1.3;margin-bottom:0.2rem;font-weight:500;}
.today-followup-meta{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);letter-spacing:0.02em;}
.due-today{color:#e05252 !important;}
.due-soon{color:#e09a52 !important;}
/* Bill rows */
.today-bill{padding:0.55rem 1.1rem;border-bottom:1px solid rgba(255,255,255,0.03);cursor:pointer;transition:background 0.12s;}
.today-bill:hover{background:var(--surface2);}
.today-bill:last-child{border-bottom:none;}
.today-bill-num{font-family:var(--font-mono);font-size:0.65rem;color:var(--gold);margin-bottom:0.1rem;}
.today-bill-title{font-size:0.8rem;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.3;}
.today-bill-meta{font-family:var(--font-mono);font-size:0.67rem;color:var(--muted);margin-top:0.12rem;letter-spacing:0.02em;}
.today-bill-moved{font-family:var(--font-mono);font-size:0.5rem;padding:0.08rem 0.3rem;border-radius:3px;background:rgba(46,160,67,0.12);color:#4ade80;border:1px solid rgba(46,160,67,0.2);margin-left:0.35rem;vertical-align:middle;}
.today-empty{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);text-align:center;padding:1.5rem 1rem;font-style:italic;line-height:1.6;}
.today-weekly-link{padding:0.5rem 1.1rem;border-top:1px solid var(--border);flex-shrink:0;}
.today-weekly-link button{background:none;border:none;font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);cursor:pointer;padding:0;letter-spacing:0.04em;}
.today-weekly-link button:hover{opacity:0.75;}
[data-theme="light"] .today-view{background:#f8f9fc;}
[data-theme="light"] .today-col{background:#f8f9fc;}
[data-theme="light"] .today-col-head{background:#f0f3f9;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .today-col-title{color:#1a1e2e;}
[data-theme="light"] .today-event-name{color:#1a1e2e;}
[data-theme="light"] .today-bill-title{color:#1a1e2e;}
[data-theme="light"] .today-followup-task{color:#1a1e2e;}
[data-theme="light"] .today-event{border-color:rgba(26,39,68,0.05);}
[data-theme="light"] .today-bill{border-color:rgba(26,39,68,0.05);}
[data-theme="light"] .today-followup{border-color:rgba(26,39,68,0.05);}
[data-theme="light"] .today-bill:hover{background:#e8ecf4;}

/* ── TUTORIAL OVERLAY ── */
.tutorial-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:500;display:flex;align-items:center;justify-content:center;padding:2rem;backdrop-filter:blur(4px);}
.tutorial-modal{background:var(--charcoal,#16161f);border:1px solid rgba(201,168,76,0.3);border-radius:18px;padding:0;max-width:540px;width:100%;overflow:hidden;position:relative;}
.tutorial-modal::before{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);}
.tutorial-head{padding:2rem 2rem 1.25rem;text-align:center;}
.tutorial-wordmark{font-family:var(--font-display);font-size:1.5rem;font-weight:900;color:var(--gold);margin-bottom:0.5rem;}
.tutorial-wordmark em{font-style:italic;}
.tutorial-subtitle{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.08em;}
.tutorial-steps{padding:0 2rem 1.5rem;display:flex;flex-direction:column;gap:0.75rem;}
.tutorial-step{display:flex;align-items:flex-start;gap:0.85rem;padding:0.85rem 1rem;background:var(--surface2,#1e1e2a);border:1px solid var(--border);border-radius:10px;cursor:pointer;transition:all 0.18s;}
.tutorial-step:hover{border-color:var(--gold-dim);background:rgba(201,168,76,0.05);}
.tutorial-step-num{width:26px;height:26px;border-radius:50%;background:rgba(201,168,76,0.12);border:1px solid rgba(201,168,76,0.3);color:var(--gold);font-family:var(--font-mono);font-size:0.7rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;}
.tutorial-step-text{flex:1;}
.tutorial-step-title{font-size:0.88rem;font-weight:600;color:var(--text);margin-bottom:0.2rem;}
.tutorial-step-sub{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);line-height:1.5;}
.tutorial-step-arrow{color:var(--gold);font-size:0.85rem;flex-shrink:0;margin-top:2px;opacity:0.6;}
.tutorial-foot{padding:1rem 2rem 1.5rem;display:flex;justify-content:center;}
.tutorial-dismiss{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.5rem 1.5rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);cursor:pointer;transition:all 0.18s;}
.tutorial-dismiss:hover{border-color:var(--gold-dim);color:var(--text);}
[data-theme="light"] .tutorial-modal{background:#f8f9fc;}

/* ── PRIVACY MODAL ────────────────────────────────────────────────────── */
.privacy-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:1.5rem;}
.privacy-modal{background:var(--surface);border:1px solid var(--border);border-radius:12px;width:100%;max-width:700px;max-height:85vh;display:flex;flex-direction:column;overflow:hidden;}
.privacy-modal-head{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.privacy-modal-title{font-size:0.95rem;font-weight:700;color:var(--text);}
.privacy-modal-updated{font-size:0.65rem;color:var(--text2);opacity:0.6;margin-top:2px;}
.privacy-modal-body{padding:1.5rem;overflow-y:auto;flex:1;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.privacy-modal-body h2{font-size:0.75rem;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:var(--gold);margin:1.5rem 0 0.5rem;border-bottom:1px solid var(--border);padding-bottom:0.35rem;}
.privacy-modal-body h2:first-child{margin-top:0;}
.privacy-modal-body p,.privacy-modal-body li{font-size:0.8rem;color:var(--text2);line-height:1.7;margin-bottom:0.5rem;}
.privacy-modal-body ul{padding-left:1.25rem;margin-bottom:0.5rem;}
.privacy-modal-body strong{color:var(--text);font-weight:600;}
.privacy-modal-body a{color:var(--gold);text-decoration:none;}
.privacy-modal-disclaimer{margin-top:1rem;padding:0.75rem 1rem;background:var(--surface2);border-radius:8px;font-size:0.7rem;color:var(--text2);line-height:1.6;}
.privacy-modal-foot{padding:1rem 1.5rem;border-top:1px solid var(--border);display:flex;justify-content:flex-end;flex-shrink:0;}
`;

const NAV_ITEMS = [
  { key:"dashboard",          label:"Dashboard"             },
  null,
  { key:"platform",           label:"Platform"              },
  { key:"clients",            label:"Client Services"          },
  { key:"calendar",           label:"Legislative Calendar"  },
  { key:"billtracker",        label:"Bill Search"           },
  { key:"trackedlegislation", label:"Tracked Bills"         },
  { key:"billstudio",         label:"Bill Studio"           },
  { key:"map",                label:"District Map"          },
  { key:"finance",            label:"Campaign Finance"      },
  { key:"directory",          label:"Directory"             },
];


const TAG_COLORS = {
  BUDGET:{ bg:"rgba(201,168,76,0.08)", border:"rgba(201,168,76,0.2)", color:"var(--gold)" },
  EDUCATION:{ bg:"rgba(59,111,212,0.08)", border:"rgba(59,111,212,0.2)", color:"#6b9eff" },
  HEALTH:{ bg:"rgba(46,125,82,0.1)", border:"rgba(46,125,82,0.25)", color:"#4ade80" },
  ENVIRONMENT:{ bg:"rgba(34,197,94,0.08)", border:"rgba(34,197,94,0.2)", color:"#86efac" },
  ECONOMY:{ bg:"rgba(251,191,36,0.08)", border:"rgba(251,191,36,0.2)", color:"#fbbf24" },
  POLITICS:{ bg:"rgba(139,92,246,0.08)", border:"rgba(139,92,246,0.2)", color:"#c4b5fd" },
  CRIME:{ bg:"rgba(239,68,68,0.08)", border:"rgba(239,68,68,0.2)", color:"#fca5a5" },
  INFRASTRUCTURE:{ bg:"rgba(14,165,233,0.08)", border:"rgba(14,165,233,0.2)", color:"#7dd3fc" },
  ENERGY:{ bg:"rgba(234,179,8,0.08)", border:"rgba(234,179,8,0.2)", color:"#fde047" },
  HOUSING:{ bg:"rgba(249,115,22,0.08)", border:"rgba(249,115,22,0.2)", color:"#fdba74" },
};

function formatStoryDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month:"short", day:"numeric" });
}

function NewsStory({ s }) {
  return (
    <div className="story">
      <a className="story-link" href={s.link} target="_blank" rel="noreferrer">{s.title}</a>
      {s.description && <p className="story-excerpt">{s.description}</p>}
      <div className="story-footer">
        <div className="story-tags">
          {(s.tags||[]).map(tag => {
            const c = TAG_COLORS[tag] || TAG_COLORS.POLITICS;
            return <span key={tag} className="story-tag" style={{ background:c.bg, borderColor:c.border, color:c.color }}>{tag}</span>;
          })}
        </div>
        <div className="story-byline">
          <span className="story-source">{s.source}</span>
          {s.pubDate && <><div className="story-sep"/><span className="story-time">{formatStoryDate(s.pubDate)}</span></>}
        </div>
      </div>
    </div>
  );
}

/* ── COMMITTEE INFO MODAL ─────────────────────────────── */
function InfoModal({ committee, onClose, onNavigate, userId }) {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientsWithBills, setClientsWithBills] = useState([]);
  const staticData = MI_COMMITTEES.find(c => c.id === committee.committee_id);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${SERVER}/api/calendar/meetings`);
        const d = await r.json();
        const name = committee.committee_name.toLowerCase();
        const match = (d.meetings || []).find(m =>
          m.committee?.toLowerCase() === name ||
          m.committee?.toLowerCase().includes(name) ||
          name.includes(m.committee?.toLowerCase())
        );
        if (match) {
          const dr = await fetch(`${SERVER}/api/calendar/meeting/${match.id}`);
          const dd = await dr.json();
          setMeeting({ ...match, ...dd });
        } else setMeeting(null);
      } catch { setMeeting(null); }
      setLoading(false);
    })();
  }, [committee.committee_id]);

  // Fetch clients tracking bills currently in this committee
  useEffect(() => {
    if (!userId) return;
    const name = committee.committee_name.toLowerCase();
    supabase
      .from("client_bills")
      .select("bill_number, title, position, clients(name), committee")
      .then(({ data }) => {
        if (!data) return;
        // committee is JSONB: {committee_name: "..."}
        const matches = data.filter(b => {
          const cn = (b.committee?.committee_name || "").toLowerCase();
          return cn === name || cn.includes(name) || name.includes(cn);
        });
        // Group by client
        const byClient = {};
        matches.forEach(b => {
          const clientName = b.clients?.name;
          if (!clientName) return;
          if (!byClient[clientName]) byClient[clientName] = [];
          byClient[clientName].push({ bill_number: b.bill_number, title: b.title, position: b.position });
        });
        setClientsWithBills(Object.entries(byClient));
      });
  }, [committee.committee_id, userId]);

  return (
    <div className="overlay" onClick={onClose}>
      <div className="info-modal" onClick={e => e.stopPropagation()}>
        <div className="info-head">
          <div className={`info-chamber ${committee.chamber}`}>{committee.chamber} Committee</div>
          <div className="info-name">{committee.committee_name}</div>
          <div style={{display:"flex",gap:"0.4rem",flexWrap:"wrap"}}>
            <span className={`badge ${committee.chamber}`}>{committee.chamber}</span>
            <span className="badge type">{committee.type}</span>
          </div>
        </div>
        <div className="info-body">
          <div>
            <div className="info-section">Next Scheduled Meeting</div>
            {loading ? <div className="loading-row"><div className="spin"/></div>
            : meeting ? (
              <>
                <div className="info-grid">
                  <div className="info-cell"><div className="info-cell-label">Date</div><div className="info-cell-value">{meeting.date ? new Date(meeting.date).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"}) : "—"}</div></div>
                  <div className="info-cell"><div className="info-cell-label">Time</div><div className="info-cell-value">{meeting.meetTime || meeting.time || "—"}</div></div>
                  <div className="info-cell" style={{gridColumn:"span 2"}}><div className="info-cell-label">Location</div><div className="info-cell-value">{meeting.location || staticData?.location || "—"}</div></div>
                  {meeting.chair && <div className="info-cell" style={{gridColumn:"span 2"}}><div className="info-cell-label">Chair</div><div className="info-cell-value">{meeting.chair}</div></div>}
                </div>
                {meeting.bills?.length > 0 && (
                  <div style={{marginTop:"1rem"}}>
                    <div className="info-section">Agenda — {meeting.bills.length} Item{meeting.bills.length!==1?"s":""}</div>
                    {meeting.bills.map((b,i) => (
                      <div className="info-agenda" key={i}>
                        {b.type==="bill"?<><div className="info-agenda-num">{b.number}</div><div>{b.description||"No description."}</div></>:<div style={{fontStyle:"italic",color:"var(--muted)"}}>{b.description}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : <div className="info-no-meeting">No upcoming meeting currently scheduled.</div>}
          </div>
          {clientsWithBills.length > 0 && (
            <div>
              <div className="info-section">Client Interest</div>
              {clientsWithBills.map(([clientName, bills]) => (
                <div key={clientName} style={{marginBottom:"0.75rem"}}>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--gold)",fontWeight:600,marginBottom:"0.3rem",display:"flex",alignItems:"center",gap:"0.4rem"}}>
                    <span style={{display:"inline-block",width:"6px",height:"6px",borderRadius:"50%",background:"var(--gold)",flexShrink:0}}/>
                    {clientName}
                  </div>
                  {bills.map((b, i) => (
                    <div key={i} style={{display:"flex",alignItems:"baseline",gap:"0.5rem",padding:"0.2rem 0 0.2rem 0.9rem",borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                      <span style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--gold)",flexShrink:0}}>{b.bill_number}</span>
                      <span style={{fontSize:"0.78rem",color:"var(--text)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.title}</span>
                      {b.position && <span style={{fontFamily:"var(--font-mono)",fontSize:"0.55rem",padding:"0.1rem 0.35rem",borderRadius:"3px",flexShrink:0,background:b.position==="support"?"rgba(46,125,82,0.12)":b.position==="oppose"?"rgba(192,57,43,0.1)":"rgba(201,168,76,0.08)",color:b.position==="support"?"#27ae60":b.position==="oppose"?"#e74c3c":"var(--gold)",border:`1px solid ${b.position==="support"?"rgba(46,125,82,0.25)":b.position==="oppose"?"rgba(192,57,43,0.2)":"rgba(201,168,76,0.2)"}`}}>{b.position.toUpperCase()}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {staticData?.members?.length > 0 && (
            <div>
              <div className="info-section">Members ({staticData.members.length})</div>
              {staticData.members.map((m,i) => (
                <div className="info-member" key={i}>
                  <span className="info-member-name">{m.name}</span>
                  <span className={`info-member-role${m.role==="Chair"?" chair":""}`}>{m.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="info-foot">
          <div style={{display:"flex",gap:"0.75rem"}}>
            {meeting && <button className="btn-ics" onClick={() => window.open(`${SERVER}/api/calendar/ics/${meeting.id}`,"_blank")}>📥 Add to Calendar</button>}
            {meeting?.link && <a className="btn-leg" href={meeting.link} target="_blank" rel="noreferrer">🔗 Legislature Site</a>}
            {meeting && <button className="btn-ics" style={{background:"var(--gold)",color:"#09090e",border:"none"}} onClick={() => { sessionStorage.setItem("wc_calendar_meeting_id", meeting.id); onClose(); onNavigate("calendar"); }}>📅 View in Calendar →</button>}
          </div>
          <button className="btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── COMMITTEE SEARCH MODAL ───────────────────────────── */
function SearchModal({ followedIds, onFollow, onUnfollow, onClose, fromTutorial, onBackToTutorial }) {
  const [query, setQuery] = useState("");
  const [chamber, setChamber] = useState("All");
  const [type, setType] = useState("All");
  const results = MI_COMMITTEES.filter(c => {
    const mq = !query || c.name.toLowerCase().includes(query.toLowerCase());
    const mc = chamber === "All" || c.chamber === chamber;
    const mt = type === "All" || c.type === type;
    return mq && mc && mt;
  });
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",gap:"0.75rem",marginBottom:"1rem"}}>
          {fromTutorial && onBackToTutorial && (
            <button onClick={onBackToTutorial} style={{background:"none",border:"none",color:"var(--muted)",fontFamily:"var(--font-mono)",fontSize:"0.7rem",cursor:"pointer",padding:0}}>← Back</button>
          )}
          <div className="modal-title" style={{margin:0}}>Follow Committees</div>
        </div>
        <input className="modal-input" placeholder="Search committees…" value={query} onChange={e => setQuery(e.target.value)} autoFocus />
        <div className="modal-filters">
          {["All","House","Senate"].map(f => <button key={f} className={`filter-chip${chamber===f?" active":""}`} onClick={() => setChamber(f)}>{f}</button>)}
          <div style={{width:"1px",background:"var(--border)",margin:"0 0.2rem"}}/>
          {["All","Standing","Subcommittee"].map(f => <button key={f} className={`filter-chip${type===f?" active":""}`} onClick={() => setType(f)}>{f}</button>)}
        </div>
        <div className="modal-results">
          {results.length === 0 && <div style={{fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",textAlign:"center",padding:"1rem"}}>No committees found.</div>}
          {results.map(c => {
            const on = followedIds.has(c.id);
            return (
              <div className="result-item" key={c.id}>
                <div className="result-name">{c.name}</div>
                <div className="result-meta">
                  <span className={`badge ${c.chamber}`}>{c.chamber}</span>
                  <span className="badge type">{c.type}</span>
                  <button className={`btn-follow${on?" on":""}`} onClick={() => on ? onUnfollow(c.id) : onFollow(c)}>{on ? "✓ Following" : "+ Follow"}</button>
                </div>
              </div>
            );
          })}
        </div>
        <button className="btn-close" onClick={fromTutorial && onBackToTutorial ? onBackToTutorial : onClose}>Done</button>
      </div>
    </div>
  );
}

/* ── WEEKLY BILLS MODAL ───────────────────────────────── */
const PULSE_TIERS = [
  { max:15,  label:"Flatline",     color:"#6a6860", bpm:0   },
  { max:30,  label:"Resting",      color:"#4a7fa5", bpm:55  },
  { max:50,  label:"Steady Pulse", color:"#2e8b6e", bpm:70  },
  { max:65,  label:"Elevated",     color:"#c9a84c", bpm:95  },
  { max:82,  label:"Racing",       color:"#d47a2a", bpm:140 },
  { max:100, label:"Code Red",     color:"#c0392b", bpm:200 },
];

function PulseReportModal({ pulse, onClose }) {
  if (!pulse) return null;
  const metrics = [
    {
      label:"Bill Introductions",
      value: pulse.bills,
      weight: 25,
      baseline: 30,
      hot: 70,
      score: Math.round(Math.min(pulse.bills / 70, 1) * 100),
      desc:"New bills introduced this week across both chambers. Updates in real-time from the WhipCount database.",
    },
    {
      label:"Bills with Action",
      value: pulse.moved,
      weight: 45,
      baseline: 15,
      hot: 40,
      score: Math.round(Math.min(pulse.moved / 40, 1) * 100),
      desc:"Bills that advanced, were amended, assigned to committee, or otherwise moved this week. The strongest available real-time signal of legislative activity.",
    },
    {
      label:"Committee Meetings",
      value: pulse.meetings,
      weight: 30,
      baseline: 10,
      hot: 22,
      score: Math.round(Math.min(pulse.meetings / 22, 1) * 100),
      desc:"Committee meetings that occurred this week across both chambers. Captured from the Michigan Legislature's live RSS feed, updated every 30 minutes.",
    },
  ];

  return (
    <div className="overlay" onClick={onClose}>
      <div className="weekly-modal" style={{maxWidth:"580px"}} onClick={e => e.stopPropagation()}>
        <div className="weekly-head" style={{borderBottom:"1px solid var(--border)"}}>
          <div className="weekly-title">This Week's Session Activity</div>
          <div className="modal-sub" style={{display:"flex",alignItems:"center",gap:"0.5rem",flexWrap:"wrap",marginBottom:"0.25rem"}}>
            <span style={{fontFamily:"var(--font-display)",fontSize:"1.4rem",fontWeight:900,color:pulse.color}}>{pulse.score}</span>
            <span style={{fontFamily:"var(--font-mono)",fontSize:"0.7rem",color:pulse.color,fontWeight:600,letterSpacing:"0.08em"}}>{pulse.label.toUpperCase()}</span>
            {pulse.week_start && <span style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)",marginLeft:"0.5rem"}}>Week of {new Date(pulse.week_start + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>}
          </div>
          <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)",lineHeight:1.5,marginTop:"0.5rem"}}>
            Pulse is calculated from three real-time metrics weighted by legislative significance. Resets every Monday at midnight ET.
          </div>
          
        </div>

        <div className="weekly-body" style={{padding:"1.25rem 1.5rem",display:"flex",flexDirection:"column",gap:"1rem"}}>

          {/* Metric breakdown */}
          {metrics.map(m => (
            <div key={m.label} style={{display:"flex",flexDirection:"column",gap:"0.4rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <div style={{fontFamily:"var(--font-mono)",fontSize:"0.72rem",color:"var(--text)",fontWeight:600}}>{m.label}</div>
                <div style={{display:"flex",gap:"0.75rem",fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--muted)"}}>
                  <span style={{color:"var(--text)",fontWeight:600}}>{m.value}</span>
                  <span>weight: {m.weight}%</span>
                </div>
              </div>
              <div style={{height:"6px",background:"var(--surface2)",borderRadius:"100px",overflow:"hidden"}}>
                <div style={{height:"100%",width:m.score+"%",background:pulse.color,borderRadius:"100px",transition:"width 0.6s ease"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)"}}>
                <span>{m.desc}</span>
                <span>baseline {m.baseline} · hot {m.hot}+</span>
              </div>
            </div>
          ))}

          {/* Tier legend */}
          <div style={{borderTop:"1px solid var(--border)",paddingTop:"1rem",marginTop:"0.25rem"}}>
            <div style={{fontFamily:"var(--font-mono)",fontSize:"0.6rem",color:"var(--muted)",letterSpacing:"0.1em",marginBottom:"0.6rem"}}>ACTIVITY TIERS</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"0.4rem"}}>
              {PULSE_TIERS.map(t => (
                <div key={t.label} style={{
                  display:"flex",alignItems:"center",gap:"0.4rem",padding:"0.4rem 0.6rem",
                  borderRadius:"6px",border:"1px solid",
                  borderColor: pulse.label === t.label ? t.color : "var(--border)",
                  background: pulse.label === t.label ? t.color+"18" : "transparent",
                }}>
                  <div style={{width:"8px",height:"8px",borderRadius:"50%",background:t.color,flexShrink:0}}/>
                  <div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:t.color,fontWeight:600}}>{t.label}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:"0.55rem",color:"var(--muted)"}}>{t.max===15?"0–15":t.max===30?"16–30":t.max===50?"31–50":t.max===65?"51–65":t.max===82?"66–82":"83–100"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="weekly-foot"><button className="btn-close" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function PAModal({ pas, onClose }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div className="weekly-modal" onClick={e => e.stopPropagation()}>
        <div className="weekly-head">
          <div className="weekly-title">Public Acts This Month</div>
          <div className="modal-sub">{pas.length} bill{pas.length !== 1 ? "s" : ""} signed into law this month</div>
        </div>
        <div className="weekly-body">
          {pas.length === 0 ? (
            <div style={{padding:"2rem",textAlign:"center",fontFamily:"var(--font-mono)",fontSize:"0.7rem",color:"var(--muted)"}}>No public acts signed this month yet.</div>
          ) : pas.map(b => (
            <div className="weekly-bill" key={b.bill_id}>
              <div className="weekly-number">{b.bill_number}</div>
              <div className="weekly-title-text">{b.title}</div>
              <div className="weekly-meta">
                PA Signed{b.last_action_date ? ` · ${new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}` : ""}
              </div>
            </div>
          ))}
        </div>
        <div className="weekly-foot"><button className="btn-close" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function WeeklyModal({ bills, onClose, onTrack, trackedIds }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div className="weekly-modal" onClick={e => e.stopPropagation()}>
        <div className="weekly-head">
          <div className="weekly-title">Bills Introduced This Week</div>
          <div className="modal-sub">{bills.length} bill{bills.length!==1?"s":""} in the last 7 days</div>
        </div>
        <div className="weekly-body">
          {bills.map(b => {
            const isTracked = trackedIds && trackedIds.has(b.bill_id);
            return (
              <div className="weekly-bill" key={b.bill_id} style={{display:"flex",alignItems:"flex-start",gap:"0.75rem"}}>
                <div style={{flex:1}}>
                  <div className="weekly-number">{b.bill_number}</div>
                  <div className="weekly-title-text">{b.title}</div>
                  <div className="weekly-meta">{displayStatus(b.status)}{b.last_action_date ? ` · ${new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}` : ""}</div>
                </div>
                <button
                  onClick={() => !isTracked && onTrack(b)}
                  style={{
                    flexShrink:0,marginTop:"0.15rem",
                    background: isTracked ? "rgba(46,125,82,0.12)" : "var(--gold-glow)",
                    border: isTracked ? "1px solid rgba(46,125,82,0.3)" : "1px solid var(--gold-dim)",
                    borderRadius:"6px",padding:"0.28rem 0.65rem",
                    fontFamily:"var(--font-mono)",fontSize:"0.6rem",
                    color: isTracked ? "#4ade80" : "var(--gold)",
                    cursor: isTracked ? "default" : "pointer",
                    transition:"all 0.18s",whiteSpace:"nowrap"
                  }}
                >
                  {isTracked ? "✓ Tracked" : "+ Track"}
                </button>
              </div>
            );
          })}
        </div>
        <div className="weekly-foot"><button className="btn-close" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

/* ── MAIN ─────────────────────────────────────────────── */
export default function DashboardPage({ onNavigate, session, profile, currentPage = "dashboard" }) {
  const [trackedBills,        setTrackedBills]        = useState([]);
  const [followedCommittees,  setFollowedCommittees]  = useState([]);
  const [upcomingMeetings,    setUpcomingMeetings]    = useState([]);
  const [weeklyBills,         setWeeklyBills]         = useState([]);
  const [loadingBills,        setLoadingBills]        = useState(true);
  const [loadingCommittees,   setLoadingCommittees]   = useState(true);
  const [loadingMeetings,     setLoadingMeetings]     = useState(true);
  const [loadingWeekly,       setLoadingWeekly]       = useState(true);
  const [showSearch,          setShowSearch]          = useState(false);
  const [eventsFilter,        setEventsFilter]        = useState("yours"); // 'yours' | 'all'
  const [clientFollowUps,     setClientFollowUps]     = useState([]);
  const [showTutorial,        setShowTutorial]        = useState(false);
  const [tutorialFrom,        setTutorialFrom]        = useState(false);
  const [ekgHover,            setEkgHover]            = useState(false);
  const [fundraisers,         setFundraisers]         = useState([]);
  const [infoCommittee,       setInfoCommittee]       = useState(null);
  const [showWeekly,          setShowWeekly]          = useState(false);
  const [lightMode,           setLightMode]           = useState(() => localStorage.getItem("wc_theme") !== "dark");
  const [showOnboarding, setShowOnboarding] = useState(!localStorage.getItem("wc_onboarding_dismissed"));

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", lightMode ? "light" : "dark");
    localStorage.setItem("wc_theme", lightMode ? "light" : "dark");
  }, [lightMode]);
  const [dismissedMoved,      setDismissedMoved]      = useState(new Set());
  const [selectedBill,          setSelectedBill]          = useState(null);
  const [billClients,           setBillClients]           = useState([]);
  const [selectedFundraiser,    setSelectedFundraiser]    = useState(null);
  const [pulse,               setPulse]               = useState(null);
  const [loadingPulse,        setLoadingPulse]        = useState(true);
  const [showPAs,             setShowPAs]             = useState(false);
  const [showPulseReport,     setShowPulseReport]     = useState(false);
  const [monthlyPAs,          setMonthlyPAs]          = useState([]);
  const [loadingPAs,          setLoadingPAs]          = useState(true);
  const [news,                setNews]                = useState(null);
  const [loadingNews,         setLoadingNews]         = useState(true);
  const [newsError,           setNewsError]           = useState(null);
  const [showPrivacy,         setShowPrivacy]         = useState(false);
  const [showTos,             setShowTos]             = useState(false);
  const [confirmState,        setConfirmState]        = useState(null); // {message,subtext,onConfirm}
  const { toasts, toast, dismiss } = useToast();
  const confirm = (message, subtext, onConfirm) => setConfirmState({ message, subtext, onConfirm });

  const userId        = session?.user?.id;
  const firstName     = profile?.full_name?.split(" ")[0] || "Welcome";

  // Show tutorial only once — persists to Supabase profile so it survives localStorage clears
  useEffect(() => {
    if (!profile) return;
    if (!localStorage.getItem("wc_tutorial_dismissed") && !profile.tutorial_dismissed) {
      setShowTutorial(true);
    }
  }, [profile]);

  const dismissTutorial = async () => {
    localStorage.setItem("wc_tutorial_dismissed", "1");
    setShowTutorial(false);
    setTutorialFrom(false);
    if (userId) {
      supabase.from("profiles").update({ tutorial_dismissed: true }).eq("id", userId);
    }
  };
  const prevLogin     = profile?.previous_last_login ? new Date(profile.previous_last_login) : null;
  const sessionEnd    = new Date("2026-12-31");
  const daysLeft      = Math.max(0, Math.ceil((sessionEnd - new Date()) / 86400000));

  // Tracked bills
  useEffect(() => {
    if (!userId) { setLoadingBills(false); return; }
    supabase.from("tracked_bills").select("*").eq("user_id", userId)
      .order("last_action_date", { ascending: false })
      .then(({ data }) => { setTrackedBills(data || []); setLoadingBills(false); });
  }, [userId]);

  // Followed committees
  useEffect(() => {
    if (!userId) { setLoadingCommittees(false); return; }
    supabase.from("followed_committees").select("*").eq("user_id", userId)
      .order("followed_at", { ascending: false })
      .then(({ data }) => { setFollowedCommittees(data || []); setLoadingCommittees(false); });
  }, [userId]);

  // Upcoming meetings for followed committees
  useEffect(() => {
    if (followedCommittees.length === 0) { setUpcomingMeetings([]); setLoadingMeetings(false); return; }
    setLoadingMeetings(true);
    fetch(`${SERVER}/api/calendar/meetings`)
      .then(r => r.json())
      .then(d => {
        // Normalize committee names for matching — strip "Committee on", "Standing", punctuation
        const normalize = s => (s || "").toLowerCase()
          .replace(/^(joint\s+)?(sub)?committee\s+(on\s+)?/i, "")
          .replace(/^standing\s+/i, "")
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, " ")
          .trim();
        const names = followedCommittees.map(c => normalize(c.committee_name));
        const matched = (d.meetings || []).filter(m => {
          const mc = normalize(m.committee);
          if (!mc) return false;
          return names.some(n =>
            mc === n ||
            mc.includes(n) ||
            n.includes(mc) ||
            // word-level overlap: at least 2 significant words match
            mc.split(" ").filter(w => w.length > 3).some(w => n.includes(w))
          );
        }).slice(0, 8);
        setUpcomingMeetings(matched);
        setLoadingMeetings(false);
      })
      .catch(() => setLoadingMeetings(false));
  }, [followedCommittees]);

  // Weekly bills
  useEffect(() => {
    fetch(`${PROXY}/api/db/masterlist`)
      .then(r => r.json())
      .then(d => {
        const weekAgo = new Date(Date.now() - 7 * 86400000);
        const bills = (d.bills || []).filter(b => b.last_action_date && new Date(b.last_action_date) >= weekAgo);
        setWeeklyBills(bills);
        setLoadingWeekly(false);
      })
      .catch(() => setLoadingWeekly(false));
  }, []);

  // Monthly Public Acts
  useEffect(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0,0,0,0);
    supabase.from("bills")
      .select("bill_id,bill_number,title,status,status_text,last_action,last_action_date")
      .or("status.eq.3,status_text.eq.Enrolled,status_text.eq.Chaptered")
      .gte("last_action_date", monthStart.toISOString().split("T")[0])
      .order("last_action_date", { ascending: false })
      .then(({ data }) => { setMonthlyPAs(data || []); setLoadingPAs(false); });
  }, []);

  // Session Pulse
  useEffect(() => {
    const CACHE_KEY = "wc_pulse_cache_v5";
    const CACHE_TS  = "wc_pulse_ts_v5";
    const TTL = 15 * 60 * 1000;
    // Clear old cache keys
    try { ["wc_pulse_cache","wc_pulse_ts","wc_pulse_cache_v2","wc_pulse_ts_v2","wc_pulse_cache_v3","wc_pulse_ts_v3","wc_pulse_cache_v4","wc_pulse_ts_v4"].forEach(k => sessionStorage.removeItem(k)); } catch {}
    try {
      const ts = parseInt(sessionStorage.getItem(CACHE_TS) || "0");
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached && Date.now() - ts < TTL) {
        const parsed = JSON.parse(cached);
        // Validate cache has current fields — if it has votes but not moved, it's stale
        if (parsed.moved !== undefined) {
          setPulse(parsed);
          setLoadingPulse(false);
          return;
        }
      }
    } catch {}

    fetch(`${SERVER}/api/pulse`)
      .then(r => r.json())
      .then(({ bills = 0, moved = 0, meetings = 0, week_start = null, updated_at = null }) => {

      const bScore = Math.min(bills    / 70, 1) * 100 * 0.25;
      const vScore = Math.min(moved    / 40, 1) * 100 * 0.45;
      const mScore = Math.min(meetings / 22, 1) * 100 * 0.30;
      const score  = Math.round(bScore + vScore + mScore);

      const TIERS = [
        { max:15,  label:"Flatline",     color:"#6a6860", bpm:0   },
        { max:30,  label:"Resting",      color:"#4a7fa5", bpm:55  },
        { max:50,  label:"Steady Pulse", color:"#2e8b6e", bpm:70  },
        { max:65,  label:"Elevated",     color:"#c9a84c", bpm:95  },
        { max:82,  label:"Racing",       color:"#d47a2a", bpm:140 },
        { max:100, label:"Code Red",     color:"#c0392b", bpm:200 },
      ];
      const tier = TIERS.find(t => score <= t.max) || TIERS[TIERS.length - 1];
      const data = { score, bills, moved, meetings, week_start, updated_at, ...tier };
      setPulse(data);
      setLoadingPulse(false);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        sessionStorage.setItem(CACHE_TS, String(Date.now()));
      } catch {}
      })
      .catch(() => setLoadingPulse(false));
  }, []);

  // News feed — cached in sessionStorage for 20 minutes
  useEffect(() => {
    const NEWS_CACHE_KEY = "wc_news_cache";
    const NEWS_CACHE_TS  = "wc_news_cache_ts";
    const TTL = 20 * 60 * 1000; // 20 minutes
    try {
      const ts = parseInt(sessionStorage.getItem(NEWS_CACHE_TS) || "0");
      const cached = sessionStorage.getItem(NEWS_CACHE_KEY);
      if (cached && Date.now() - ts < TTL) {
        setNews(JSON.parse(cached));
        setLoadingNews(false);
        return;
      }
    } catch {}
    fetch(`${SERVER}/api/news`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => {
        setNews(d);
        setLoadingNews(false);
        try {
          sessionStorage.setItem(NEWS_CACHE_KEY, JSON.stringify(d));
          sessionStorage.setItem(NEWS_CACHE_TS, String(Date.now()));
        } catch {}
      })
      .catch(e => { setNewsError(e.message); setLoadingNews(false); });
  }, []);

  // Client follow-ups (for Today View column 2)
  useEffect(() => {
    if (!userId) return;
    supabase.from("client_meetings")
      .select("id,title,follow_up,follow_up_due,completed,client_id,clients(name)")
      .not("follow_up", "is", null)
      .eq("completed", false)
      .order("follow_up_due", { ascending: true })
      .then(({ data }) => setClientFollowUps(data || []));
  }, [userId]);

  // Fundraisers — only ones the user is tracking (for Today View column 1)
  useEffect(() => {
    if (!userId) return;
    supabase
      .from("tracked_fundraisers")
      .select("fundraiser_id, fundraiser_events(id, event_name, date, time_start, time_end, party, venue, address)")
      .eq("user_id", userId)
      .then(({ data }) => {
        const events = (data || [])
          .map(r => r.fundraiser_events)
          .filter(Boolean)
          .filter(e => e.date && e.date >= new Date().toISOString().slice(0, 10));
        setFundraisers(events);
      });
  }, [userId]);

  const movedBills = prevLogin
    ? new Set(trackedBills.filter(b => b.last_action_date && new Date(b.last_action_date) > prevLogin).map(b => b.bill_id))
    : new Set();

  const followedIds = new Set(followedCommittees.map(c => c.committee_id));
  const undismissedMoved = movedBills.size > 0 ? [...movedBills].filter(id => !dismissedMoved.has(id)).length : 0;

  // Sort: moved (not dismissed) first
  const sortedBills = [
    ...trackedBills.filter(b => movedBills.has(b.bill_id) && !dismissedMoved.has(b.bill_id)),
    ...trackedBills.filter(b => !(movedBills.has(b.bill_id) && !dismissedMoved.has(b.bill_id))),
  ];

  const handleFollow = async (committee) => {
    if (!userId) return;
    const { error } = await supabase.from("followed_committees").upsert({
      user_id: userId, committee_id: committee.id, committee_name: committee.name,
      chamber: committee.chamber, type: committee.type, followed_at: new Date().toISOString(),
    }, { onConflict: "user_id,committee_id" });
    if (!error) { toast("Committee followed ✓"); setFollowedCommittees(prev => [...prev, { user_id:userId, committee_id:committee.id, committee_name:committee.name, chamber:committee.chamber, type:committee.type }]); }
  };

  const handleUnfollow = async (id) => {
    await supabase.from("followed_committees").delete().eq("user_id", userId).eq("committee_id", id);
    setFollowedCommittees(prev => prev.filter(c => c.committee_id !== id));
    toast("Committee unfollowed", "info");
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onNavigate("home");
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="dash-layout">

        {/* SIDEBAR */}
        <div className="dash-sidebar">
          <div className="sidebar-brand">
            <div className="sidebar-wordmark">Whip<em>Count</em><sup>™</sup></div>
            <div className="sidebar-sub">MI Legislative Intelligence</div>
          </div>
          <div className="sidebar-nav">
            {NAV_ITEMS.map((item, i) => {
              if (!item) return <div key={i} className="sidebar-sep" />;
              const showDot = item.key === "trackedlegislation" && undismissedMoved > 0;
              return (
                <div key={item.key} className="sidebar-btn-wrap">
                  <button className={`sidebar-btn${item.key===currentPage?" active":""}`} onClick={() => {
                    if (item.key === "trackedlegislation") setDismissedMoved(movedBills);
                    onNavigate(item.key);
                  }}>
                    {item.label}
                  </button>
                  {showDot && <span className="sidebar-notif" title={`${undismissedMoved} bill${undismissedMoved!==1?"s have":" has"} moved since your last login`} />}
                </div>
              );
            })}
          </div>
          <div className="sidebar-footer">
            <button className="sidebar-foot-btn" onClick={() => onNavigate("account")}>Account</button>
            <button className="sidebar-foot-btn signout" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="dash-main">
          {/* ── PULSE BAR ── */}
          <div className="pulse-bar">
            {/* Session Pulse label */}
            <span style={{fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",letterSpacing:"0.08em",textTransform:"uppercase",flexShrink:0}}>Session Pulse</span>
            <div className="pulse-bar-divider"/>

            {/* EKG animation */}
            <div
              className="pulse-bar-ekg"
              onMouseEnter={() => setEkgHover(true)}
              onMouseLeave={() => setEkgHover(false)}
            >
              {pulse && (
                <svg viewBox="0 0 300 44" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                  {pulse.bpm === 0 ? (
                    // Flatline
                    <line x1="0" y1="22" x2="300" y2="22" stroke={pulse.color} strokeWidth="1.5" opacity="0.5"/>
                  ) : (
                    <polyline
                      fill="none"
                      stroke={pulse.color}
                      strokeWidth="1.5"
                      opacity={ekgHover ? 1 : 0.4}
                      points={
                        pulse.bpm >= 200
                          ? "0,22 20,22 25,8 30,36 35,4 40,38 45,22 65,22 70,8 75,36 80,4 85,38 90,22 110,22 115,8 120,36 125,4 130,38 135,22 155,22 160,8 165,36 170,4 175,38 180,22 200,22 205,8 210,36 215,4 220,38 225,22 245,22 250,8 255,36 260,4 265,38 270,22 300,22"
                          : pulse.bpm >= 140
                          ? "0,22 30,22 35,10 40,34 45,6 50,36 55,22 90,22 95,10 100,34 105,6 110,36 115,22 150,22 155,10 160,34 165,6 170,36 175,22 210,22 215,10 220,34 225,6 230,36 235,22 270,22 275,10 280,34 285,6 290,36 295,22 300,22"
                          : pulse.bpm >= 95
                          ? "0,22 40,22 45,12 50,32 55,8 60,34 65,22 110,22 115,12 120,32 125,8 130,34 135,22 180,22 185,12 190,32 195,8 200,34 205,22 250,22 255,12 260,32 265,8 270,34 275,22 300,22"
                          : "0,22 50,22 55,14 60,30 65,10 70,32 75,22 130,22 135,14 140,30 145,10 150,32 155,22 210,22 215,14 220,30 225,10 230,32 235,22 300,22"
                      }
                      style={ekgHover ? {
                        strokeDasharray: 600,
                        strokeDashoffset: 600,
                        animation: `ekg-draw 1.2s ease forwards, ekg-loop 2s ease ${pulse.bpm>=200?"0.5s":pulse.bpm>=140?"0.8s":"1.2s"} infinite`
                      } : {}}
                    />
                  )}
                  <style>{`
                    @keyframes ekg-draw { to { stroke-dashoffset: 0; } }
                    @keyframes ekg-loop { 0%,100%{opacity:0.9} 50%{opacity:0.4} }
                  `}</style>
                </svg>
              )}
            </div>

            {/* Score chip */}
            {pulse && (
              <div className="pulse-bar-score">
                <span className="pulse-bar-num" style={{color:pulse.color}}>{pulse.score}</span>
                <span className="pulse-bar-tier" style={{color:pulse.color}}>{pulse.label.toUpperCase()}</span>
              </div>
            )}

            <div className="pulse-bar-divider2" style={{width:"1px",height:"20px",background:"var(--border)",flexShrink:0}}/>

            {/* Activity button */}
            {pulse && (
              <button
                className="pulse-bar-btn"
                style={{borderColor:pulse.color+"66",color:pulse.color}}
                onClick={() => setShowPulseReport(true)}
              >
                This Week's Activity →
              </button>
            )}

            <div style={{flex:1}}/>

            {/* Theme toggle */}
            <button className="pulse-bar-toggle" onClick={() => setLightMode(m => !m)}>
              <span>{lightMode ? "☀️" : "🌙"}</span>
              {lightMode ? "Light Mode" : "Dark Mode"}
            </button>
          </div>
          <div className="dash-content">

            {/* ── GREETING ── */}
            <div className="dash-greeting">Good to see you, <span className="dash-greeting-name">{firstName}.</span></div>
            <div className="dash-session">103rd Michigan Legislature · {daysLeft} days left in session</div>

            {/* ── TODAY HEADER ── */}
            <div style={{background:"#1a2744",borderRadius:"12px 12px 0 0",padding:"0.85rem 1.5rem",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,marginTop:"1.25rem"}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:"1.25rem",fontWeight:800,letterSpacing:"-0.02em",color:"#e6e4dc"}}>
                Your Day at a Glance
              </div>
              <div style={{fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"rgba(255,255,255,0.5)",letterSpacing:"0.06em",textTransform:"uppercase"}}>
                {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
              </div>
            </div>
            <div className="today-view" style={{borderRadius:"0 0 12px 12px",border:"1px solid var(--border)",borderTop:"none",overflow:"hidden"}}>

              {/* Column 1 — Today's Events */}
              <div className="today-col">
                <div className="today-col-head">
                  <div className="today-col-title">Committee Meetings &amp; Fundraisers</div>
                  <div style={{display:"flex",alignItems:"center",gap:"0.4rem"}}>
                    <button className="today-col-action" onClick={()=>setShowSearch(true)}>+ Follow</button>
                    <button className="today-col-action" onClick={()=>onNavigate("calendar")}>Calendar →</button>
                  </div>
                </div>
                <div className="today-col-body">
                  {(() => {
                    if (loadingMeetings) return <div className="today-empty">Loading…</div>;

                    // Merge committee meetings + fundraisers, sort by date/time
                    // Parse M/D/YYYY (from RSS) or YYYY-MM-DD (from Supabase) → Date object
                    const parseDate = (str) => {
                      if (!str) return null;
                      // M/D/YYYY format from legislature RSS
                      const mdy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                      if (mdy) return new Date(parseInt(mdy[3]), parseInt(mdy[1])-1, parseInt(mdy[2]));
                      // YYYY-MM-DD format
                      const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                      if (ymd) return new Date(parseInt(ymd[1]), parseInt(ymd[2])-1, parseInt(ymd[3]));
                      return null;
                    };

                    const today = new Date(); today.setHours(0,0,0,0);
                    const weekAhead = new Date(today.getTime() + 7*24*60*60*1000);

                    const meetingItems = upcomingMeetings.map(m => {
                      const d = parseDate(m.date);
                      return {
                        _type: "meeting", _key: "m-" + m.id,
                        name: m.committee,
                        _dateObj: d,
                        _dateStr: d ? d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) : "",
                        time: m.time,
                        chamber: m.chamber || "House",
                        _fc: followedCommittees.find(c =>
                          (c.committee_name||"").toLowerCase().replace(/[^a-z0-9 ]/g,"").includes((m.committee||"").toLowerCase().replace(/[^a-z0-9 ]/g,"")) ||
                          (m.committee||"").toLowerCase().replace(/[^a-z0-9 ]/g,"").includes((c.committee_name||"").toLowerCase().replace(/[^a-z0-9 ]/g,""))
                        ),
                      };
                    }).filter(m => m._dateObj && m._dateObj >= today && m._dateObj <= weekAhead);

                    const fundraiserItems = fundraisers.map(f => {
                      const d = parseDate(f.date);
                      return {
                        _type: "fundraiser", _key: "f-" + f.id,
                        name: f.event_name,
                        _dateObj: d,
                        _dateStr: d ? d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) : "",
                        time: f.time_start,
                        _party: f.party,
                      };
                    }).filter(f => f._dateObj && f._dateObj >= today && f._dateObj <= weekAhead);

                    const allItems = [...meetingItems, ...fundraiserItems].sort((a, b) => {
                      if (!a._dateObj) return 1;
                      if (!b._dateObj) return -1;
                      const diff = a._dateObj - b._dateObj;
                      if (diff !== 0) return diff;
                      return (a.time || "") < (b.time || "") ? -1 : 1;
                    });

                    if (allItems.length === 0) return (
                      <div className="today-empty">
                        No upcoming events for followed committees.
                        <br/>
                        <button style={{marginTop:"0.5rem",background:"none",border:"none",color:"var(--gold)",fontFamily:"var(--font-mono)",fontSize:"0.62rem",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:"2px"}} onClick={()=>setShowSearch(true)}>Follow Committees →</button>
                      </div>
                    );

                    return allItems.map(item => {
                      const isFundraiser = item._type === "fundraiser";
                      const pipColor = isFundraiser
                        ? (item._party === "R" ? "#c0392b" : "var(--house)")
                        : (item.chamber === "Senate" ? "var(--senate)" : "var(--house)");
                      return (
                        <div key={item._key} className="today-event">
                          <div className="today-event-pip" style={{background: pipColor, marginTop:"0.3rem", width:"6px", height:"6px", borderRadius:"50%", flexShrink:0}}/>
                          <div className="today-event-info">
                            <div className="today-event-name">
                              {item.name}
                              {isFundraiser && <span style={{fontFamily:"var(--font-mono)",fontSize:"0.5rem",marginLeft:"0.4rem",padding:"0.08rem 0.3rem",borderRadius:"3px",background:"rgba(192,57,43,0.12)",color:"#e05252",border:"1px solid rgba(192,57,43,0.2)"}}>FUNDRAISER</span>}
                            </div>
                            <div className="today-event-meta">
                              {item._dateStr}{item.time ? ` · ${item.time}` : ""}
                            </div>
                          </div>
                          {isFundraiser ? (
                            <div className="today-event-actions">
                              <button className="today-event-btn" onClick={e=>{e.stopPropagation();setSelectedFundraiser(item);}}>Info</button>
                            </div>
                          ) : item._fc && (
                            <div className="today-event-actions">
                              <button className="today-event-btn unfollow" onClick={()=>handleUnfollow(item._fc.committee_id)}>Unfollow</button>
                              <button className="today-event-btn" onClick={()=>setInfoCommittee(item._fc)}>Info</button>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Column 2 — Follow-ups Due */}
              <div className="today-col">
                <div className="today-col-head">
                  <div className="today-col-title">Follow-ups Due</div>
                  <button className="today-col-action" onClick={()=>onNavigate("clients")}>Clients →</button>
                </div>
                <div className="today-col-body">
                  {clientFollowUps.length === 0 ? (
                    <div className="today-empty">No follow-ups pending.<br/><button style={{marginTop:"0.5rem",background:"none",border:"none",color:"var(--gold)",fontFamily:"var(--font-mono)",fontSize:"0.62rem",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:"2px"}} onClick={()=>onNavigate("clients")}>Add a Client →</button></div>
                  ) : clientFollowUps.map(m => {
                    const now = new Date();
                    const due = m.follow_up_due ? new Date(m.follow_up_due) : null;
                    const daysOut = due ? Math.ceil((due - now) / (1000*60*60*24)) : null;
                    const urgency = daysOut === null ? "" : daysOut <= 0 ? "due-today" : daysOut <= 2 ? "due-soon" : "";
                    return (
                      <div key={m.id} className="today-followup" style={{cursor:"pointer"}} onClick={()=>{sessionStorage.setItem("wc_open_client",m.client_id||"");onNavigate("clients");}}>
                        <div className="today-followup-task">{/^(none|nothing|n\/a|no follow.up set)$/i.test((m.follow_up||"").trim()) || !m.follow_up?.trim() ? "No follow-up set" : m.follow_up}</div>
                        <div className={`today-followup-meta ${urgency}`}>
                          {m.clients?.name && `${m.clients.name} · `}
                          {due ? (daysOut <= 0 ? "Due today" : daysOut === 1 ? "Due tomorrow" : `Due ${due.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`) : "No due date"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 3 — Bills */}
              <div className="today-col" style={{display:"flex",flexDirection:"column"}}>
                <div className="today-col-head">
                  <div className="today-col-title">
                    Tracked Bills
                    {movedBills.size > 0 && !dismissedMoved.size && (
                      <span style={{color:"var(--gold)",marginLeft:"0.4rem",fontSize:"0.55rem"}}>({movedBills.size} moved)</span>
                    )}
                  </div>
                  <button className="today-col-action" onClick={()=>onNavigate("trackedlegislation")}>View All →</button>
                </div>
                <div className="today-col-body">
                  {loadingBills ? (
                    <div className="today-empty">Loading…</div>
                  ) : sortedBills.length === 0 ? (
                    <div className="today-empty">No bills tracked yet.<br/><button style={{marginTop:"0.5rem",background:"none",border:"none",color:"var(--gold)",fontFamily:"var(--font-mono)",fontSize:"0.62rem",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:"2px"}} onClick={()=>onNavigate("billtracker")}>Browse Bills →</button></div>
                  ) : (
                    sortedBills.slice(0, 12).map(b => {
                      const isMoved = movedBills.has(b.bill_id) && !dismissedMoved.has(b.bill_id);
                      return (
                        <div key={b.bill_id} className="today-bill" onClick={async ()=>{
                          if(isMoved) setDismissedMoved(prev=>new Set([...prev,b.bill_id]));
                          setSelectedBill(b);
                          setBillClients([]);
                          const { data } = await supabase
                            .from("client_bills")
                            .select("clients(name)")
                            .eq("bill_id", b.bill_id);
                          setBillClients((data||[]).map(r=>r.clients?.name).filter(Boolean));
                        }}>
                          <div style={{display:"flex",alignItems:"baseline",gap:"0.3rem"}}>
                            <div className="today-bill-num">{b.bill_number}</div>
                            {isMoved && <span className="today-bill-moved">MOVED</span>}
                          </div>
                          <div className="today-bill-title">{b.nickname || b.title}</div>
                          <div className="today-bill-meta">
                            {displayStatus(b.status)}
                            {b.last_action_date ? ` · ${new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}` : ""}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {!loadingBills && sortedBills.length > 12 && (
                  <div className="today-weekly-link">
                    <button onClick={()=>onNavigate("trackedlegislation")}>
                      Showing 12 of {sortedBills.length} · View all →
                    </button>
                  </div>
                )}
                {weeklyBills.length > 0 && (
                  <div className="today-weekly-link">
                    <button onClick={()=>setShowWeekly(true)}>
                      {weeklyBills.length} new bills this week →
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* NEWS WIDGET */}
            <div className="news-widget" style={{marginTop:"1.25rem"}}>
              <div className="news-widget-header">
                <div className="news-widget-title">Legislative Intelligence Briefing</div>
                {news?.fetched && <div className="news-widget-meta">Updated {new Date(news.fetched).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>}
              </div>
              {loadingNews ? (
                <LineSkeleton count={6} />
              ) : newsError ? (
                <div className="loading-row">Failed to load news: {newsError}</div>
              ) : (
                <div className="news-cols">
                  <div className="news-col">
                    <div className="col-hdr"><div className="col-hdr-title michigan">Michigan</div><div className="col-hdr-sub">Top 20</div></div>
                    <div className="story-list">{(news?.state||[]).map((s,i) => <NewsStory key={i} s={s}/>)}</div>
                  </div>
                  <div className="news-col">
                    <div className="col-hdr"><div className="col-hdr-title national">National</div><div className="col-hdr-sub">Top 20</div></div>
                    <div className="story-list">{(news?.national||[]).map((s,i) => <NewsStory key={i} s={s}/>)}</div>
                  </div>
                </div>
              )}
            </div>

            {/* ── FOOTER ──────────────────────────────────────────────── */}
            <div className="dash-footer">
              <div className="dash-footer-grid">

                {/* Brand col */}
                <div>
                  <div className="dash-footer-col-label">Platform</div>
                  <div className="dash-footer-wordmark">Whip<em>Count</em><sup>™</sup></div>
                  <div className="dash-footer-tagline">Your one-stop platform for Michigan government affairs — bill tracking, client management, campaign finance, floor vote simulation, and AI-powered drafting.</div>
                </div>

                {/* Contact col */}
                <div>
                  <div className="dash-footer-col-label">Contact</div>
                  <div className="dash-footer-tagline" style={{marginBottom:"0.75rem"}}>Feel free to reach out during business hours with questions or support requests.</div>
                  <div className="dash-footer-contact-line">
                    <span className="dash-footer-contact-icon">✉</span>
                    <a href="mailto:admin@whipcount.us">admin@whipcount.us</a>
                  </div>
                  <div className="dash-footer-hours">Mon – Fri &nbsp;·&nbsp; 9:00 AM – 5:00 PM ET</div>
                </div>

              </div>

              {/* Bottom bar */}
              <div className="dash-footer-bottom">
                <div className="dash-footer-copy">© 2026 WhipCount™ · All Rights Reserved · whipcount.us</div>
                <div className="dash-footer-links">
                  <button className="dash-footer-link" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
                  <span className="dash-footer-sep">|</span>
                  <button className="dash-footer-link" onClick={() => setShowTos(true)}>Terms of Service</button>
                  <span className="dash-footer-sep">|</span>
                  <a className="dash-footer-link" href="https://legiscan.com" target="_blank" rel="noreferrer">Data: LegiScan</a>
                </div>
              </div>
              <div className="dash-footer-disclaimer">
                WhipCount™ is not affiliated with the Michigan Legislature or any government agency. Legislative data is sourced from LegiScan (legiscan.com) under CC BY 4.0.
              </div>
            </div>

          </div>
        </div>
      </div>

      {selectedBill && (
        <div className="overlay" onClick={() => setSelectedBill(null)}>
          <div className="bill-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="bill-detail-head">
              <div className="bill-detail-number">{selectedBill.bill_number}</div>
              <div className="bill-detail-title">{selectedBill.title}</div>
              <div className="bill-detail-badges">
                <span className={`badge ${selectedBill.chamber || "House"}`}>{selectedBill.chamber || "House"}</span>
                {selectedBill.status && (
                  <span className="bill-detail-status" style={{background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.2)",color:"var(--gold)"}}>
                    {displayStatus(selectedBill.status)}
                  </span>
                )}
              </div>
            </div>
            <div style={{padding:"0.25rem 2rem 0"}}>
              <BillStatusBadge status={selectedBill.status} lastAction={selectedBill.last_action} chamber={selectedBill.chamber} lastActionDate={selectedBill.last_action_date} />
            </div>
            <div className="bill-detail-body">
              {selectedBill.description && (
                <div className="bill-detail-row">
                  <div className="bill-detail-label">Summary</div>
                  <div className="bill-detail-value">{selectedBill.description}</div>
                </div>
              )}
              <div className="bill-detail-row">
                <div className="bill-detail-label">Last Action</div>
                <div className="bill-detail-value">{selectedBill.last_action || "—"}</div>
              </div>
              {selectedBill.last_action_date && (
                <div className="bill-detail-row">
                  <div className="bill-detail-label">Date</div>
                  <div className="bill-detail-value">
                    {new Date(selectedBill.last_action_date).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}
                  </div>
                </div>
              )}
            </div>
            <div className="bill-detail-body" style={{paddingTop:0,paddingBottom:"0.5rem"}}>
              <div className="bill-detail-row">
                <div className="bill-detail-label">Tracked For</div>
                <div className="bill-detail-value">
                  {billClients.length === 0
                    ? <span style={{color:"var(--muted)",fontStyle:"italic"}}>No clients tracking this bill</span>
                    : billClients.map((name, i) => (
                        <span key={i} style={{display:"inline-flex",alignItems:"center",background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:"100px",padding:"0.15rem 0.6rem",fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--gold)",marginRight:"0.4rem",marginBottom:"0.25rem"}}>
                          {name}
                        </span>
                      ))
                  }
                </div>
              </div>
            </div>
            <div className="bill-detail-foot">
              <button className="widget-action" onClick={() => { sessionStorage.setItem("wc_search_query", selectedBill.bill_number); sessionStorage.setItem("wc_open_bill_id", selectedBill.bill_id); setSelectedBill(null); onNavigate("billtracker"); }}>
                View in Bill Search →
              </button>
              <button className="btn-close" onClick={() => { setSelectedBill(null); setBillClients([]); }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* FUNDRAISER DETAIL MODAL */}
      {selectedFundraiser && (
        <div className="overlay" onClick={() => setSelectedFundraiser(null)}>
          <div className="bill-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="bill-detail-head">
              <div className="bill-detail-number" style={{color: selectedFundraiser._party === "R" ? "#e05252" : "var(--house)"}}>
                FUNDRAISER · {selectedFundraiser._party === "R" ? "Republican" : "Democrat"}
              </div>
              <div className="bill-detail-title">{selectedFundraiser.name}</div>
            </div>
            <div className="bill-detail-body">
              <div className="bill-detail-row">
                <div className="bill-detail-label">Date & Time</div>
                <div className="bill-detail-value">
                  {selectedFundraiser._dateStr}{selectedFundraiser.time ? ` · ${selectedFundraiser.time}` : ""}
                </div>
              </div>
              <div className="bill-detail-row">
                <div className="bill-detail-label">Tracked By</div>
                <div className="bill-detail-value" style={{fontFamily:"var(--font-mono)",fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic"}}>
                  You are tracking this event
                </div>
              </div>
            </div>
            <div className="bill-detail-foot">
              <button className="widget-action" onClick={() => { setSelectedFundraiser(null); onNavigate("calendar"); }}>
                View in Calendar →
              </button>
              <button className="btn-close" onClick={() => setSelectedFundraiser(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showSearch  && <SearchModal followedIds={followedIds} onFollow={handleFollow} onUnfollow={handleUnfollow} onClose={() => { setShowSearch(false); setTutorialFrom(false); }} fromTutorial={tutorialFrom} onBackToTutorial={() => { setShowSearch(false); setTutorialFrom(false); }} />}
      {infoCommittee && <InfoModal committee={infoCommittee} onClose={() => setInfoCommittee(null)} onNavigate={onNavigate} userId={userId} />}
      {showPAs && <PAModal pas={monthlyPAs} onClose={() => setShowPAs(false)} />}
      {showPulseReport && <PulseReportModal pulse={pulse} onClose={() => setShowPulseReport(false)} />}
      {showWeekly  && <WeeklyModal bills={weeklyBills} onClose={() => setShowWeekly(false)} trackedIds={new Set(trackedBills.map(b=>b.bill_id))} onTrack={async (b) => {
  if (!userId) return;
  const { error } = await supabase.from("tracked_bills").upsert({
    user_id: userId, bill_id: b.bill_id, bill_number: b.bill_number,
    title: b.title, status: b.status, last_action: b.last_action,
    last_action_date: b.last_action_date, change_hash: b.change_hash,
    tracked_at: new Date().toISOString(), last_seen_at: new Date().toISOString(),
  }, { onConflict: "user_id,bill_id" });
  if (!error) { setTrackedBills(prev => [...prev.filter(x=>x.bill_id!==b.bill_id), { ...b, user_id:userId }]); toast(`${b.bill_number} tracked ✓`); }
}} />}

      {showPrivacy && (
        <div className="privacy-overlay" onClick={() => setShowPrivacy(false)}>
          <div className="privacy-modal" onClick={e => e.stopPropagation()}>
            <div className="privacy-modal-head">
              <div>
                <div className="privacy-modal-title">Privacy Policy</div>
                <div className="privacy-modal-updated">Last updated: May 11, 2026</div>
              </div>
              <button className="btn-close" onClick={() => setShowPrivacy(false)}>✕</button>
            </div>
            <div className="privacy-modal-body">
              <p>We value your privacy. This Privacy Policy describes how WhipCount™ (<strong>whipcount.us</strong>) collects, uses, and protects your personal information. By accessing or using the Website, you agree to this Policy.</p>

              <h2>Information We Collect</h2>
              <p>We collect information you submit directly (name, email address) and information gathered automatically through your use of the Website (IP address, device identifiers, online identifiers, and interaction data captured via cookies and activity tracking).</p>

              <h2>How We Use Your Information</h2>
              <p>We use collected information to provide and operate the platform, manage your account, send service-related communications, detect and resolve security incidents, protect against fraudulent or malicious activity, enforce our Terms of Service, and improve the platform through analytics.</p>

              <h2>Sharing Your Information</h2>
              <p>We share your information only with trusted service providers necessary to operate the platform, including our hosting provider, authentication and database infrastructure (Supabase), email delivery service (Resend), analytics tools, and security vendors. We share information only as needed to provide services, fix errors, detect security incidents, and improve the platform.</p>

              <h2>We Do Not Sell Your Data</h2>
              <p>WhipCount™ does not sell your personal information to any third party.</p>

              <h2>How We Protect Your Information</h2>
              <p>We use SSL/TLS encryption for all data in transit, encrypt data at rest, collect only the minimum information necessary, and maintain up-to-date software and security safeguards.</p>

              <h2>Cookies</h2>
              <p>This Website uses cookies to support authentication, session management, and analytics. You may adjust cookie settings through your browser preferences.</p>

              <h2>Children's Privacy</h2>
              <p>This Website is intended for adult professional use. We do not knowingly collect personal information from children under 18.</p>

              <h2>Your Rights</h2>
              <ul>
                <li>The right to access the personal information we hold about you.</li>
                <li>The right to correct inaccurate or incomplete information.</li>
                <li>The right to request deletion of your personal information.</li>
                <li>The right to withdraw consent to our processing of your data.</li>
                <li>The right to opt out of automated decision making.</li>
              </ul>
              <p>To exercise any of these rights, contact us at <a href="mailto:admin@whipcount.us">admin@whipcount.us</a>. We will respond within 30 days.</p>

              <h2>Third-Party Links</h2>
              <p>This Website may contain links to third-party sites. We are not responsible for their content or privacy practices and encourage you to review their policies independently.</p>

              <h2>Data Storage</h2>
              <p>Your information is stored and processed in the United States.</p>

              <h2>Changes to This Policy</h2>
              <p>We may update this Privacy Policy at any time. Changes will be posted on this page with an updated date.</p>

              <h2>Contact</h2>
              <p><strong>Mason Doerr</strong> · WhipCount™<br/><a href="mailto:admin@whipcount.us">admin@whipcount.us</a> · whipcount.us</p>

              <div className="privacy-modal-disclaimer">
                <strong>Legal Disclaimer:</strong> WhipCount™ is not affiliated with the Michigan Legislature or any government agency. Legislative data is sourced from LegiScan (legiscan.com) under CC BY 4.0.
              </div>
            </div>
            <div className="privacy-modal-foot">
              <button className="widget-action" onClick={() => setShowPrivacy(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showTos && (
        <div className="privacy-overlay" onClick={() => setShowTos(false)}>
          <div className="privacy-modal" onClick={e => e.stopPropagation()}>
            <div className="privacy-modal-head">
              <div>
                <div className="privacy-modal-title">Terms of Service</div>
                <div className="privacy-modal-updated">Effective Date: May 11, 2026</div>
              </div>
              <button className="btn-close" onClick={() => setShowTos(false)}>✕</button>
            </div>
            <div className="privacy-modal-body">

              <p>Welcome to WhipCount™. These Terms of Service ("Terms") govern your access to and use of the WhipCount™ platform at whipcount.us. By using the Service, you agree to these Terms.</p>

              <h2>1. Description of the Service</h2>
              <p>WhipCount™ is a legislative intelligence and decision-support platform for tracking, analyzing, and monitoring legislative activity. It is intended solely for informational and operational support. WhipCount™ is not a law firm, lobbying firm, or government agency and does not provide legal, lobbying, political, or compliance advice. Users are solely responsible for verifying all information before relying on it.</p>

              <h2>2. Acceptance of Terms</h2>
              <p>By creating an account or using the Service you represent that you are at least 18 years old, have legal authority to enter these Terms, will comply with applicable laws, and will use the Service only for lawful purposes.</p>

              <h2>3. Account Registration</h2>
              <p>You agree to provide accurate registration information, maintain account security, and accept responsibility for all activity under your account. WhipCount™ reserves the right to approve, deny, suspend, or terminate any account at its sole discretion.</p>

              <h2>4. Acceptable Use</h2>
              <p>You agree not to: use the Service for unlawful purposes; reverse engineer or scrape the platform; use bots or automated systems without written authorization; resell or redistribute platform content; interfere with platform security; or use the Service to build a competing product.</p>

              <h2>5. Legislative Data Disclaimer</h2>
              <p>Legislative data may contain delays, omissions, or inaccuracies. WhipCount™ does not guarantee accuracy, real-time updates, or completeness of any legislative record. Users must independently verify information through official sources before taking action.</p>

              <h2>6. AI-Generated Content Disclaimer</h2>
              <p>AI-generated outputs may contain inaccuracies, omissions, or hallucinations. All AI content is for informational purposes only and does not constitute legal, political, lobbying, or regulatory advice. Users assume full responsibility for evaluating AI-generated content.</p>

              <h2>7. Intellectual Property</h2>
              <p>The Service and all associated software, branding, and content are owned by WhipCount™ or its licensors. You are granted a limited, non-exclusive, revocable license for authorized internal business use. You may not copy, resell, or create derivative works from the platform.</p>

              <h2>8. User Content</h2>
              <p>You retain ownership of content you submit. By submitting content you grant WhipCount™ a license to host, process, and use it solely to operate the Service.</p>

              <h2>9. Subscription and Billing</h2>
              <p>Paid features require a subscription. Fees are billed in advance and are non-refundable unless otherwise stated. WhipCount™ may modify pricing with reasonable notice.</p>

              <h2>10. Termination</h2>
              <p>WhipCount™ may suspend or terminate access at any time for violations of these Terms, security concerns, non-payment, or conduct harmful to the platform or its users.</p>

              <h2>11. Disclaimer of Warranties</h2>
              <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WHIPCOUNT™ DISCLAIMS ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND AVAILABILITY.</p>

              <h2>12. Limitation of Liability</h2>
              <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WHIPCOUNT™ SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES INCLUDING LOSS OF PROFITS, BUSINESS INTERRUPTION, OR RELIANCE ON PLATFORM DATA OR AI OUTPUTS. TOTAL LIABILITY SHALL NOT EXCEED AMOUNTS PAID BY YOU IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>

              <h2>13. Indemnification</h2>
              <p>You agree to defend and hold harmless WhipCount™ from claims arising from your use of the Service, violation of these Terms, or misuse of platform data or outputs.</p>

              <h2>14. Third-Party Services</h2>
              <p>The Service may rely on third-party providers. WhipCount™ is not responsible for third-party systems, uptime, or content.</p>

              <h2>15. Dispute Resolution and Arbitration</h2>
              <p>Before initiating any formal dispute, you agree to contact WhipCount™ at admin@whipcount.us and attempt informal resolution for at least 30 days. If unresolved, any dispute shall be resolved by binding individual arbitration administered by the AAA under its Commercial Arbitration Rules, in Michigan. <strong>You and WhipCount™ each waive the right to a jury trial and the right to participate in any class action or representative proceeding.</strong> Either party may seek injunctive relief in court to prevent irreparable harm pending arbitration.</p>

              <h2>16. Governing Law</h2>
              <p>These Terms are governed by the laws of the State of Michigan. Any legal proceedings shall be brought exclusively in Michigan courts.</p>

              <h2>17. Changes to These Terms</h2>
              <p>We may update these Terms at any time. Continued use after changes are posted constitutes acceptance of the revised Terms.</p>

              <h2>18. Legal Disclaimer</h2>
              <p>WhipCount™ is not affiliated with the Michigan Legislature or any governmental agency. Legislative data may be sourced from LegiScan under CC BY 4.0.</p>

              <h2>Contact</h2>
              <p><strong>Mason Doerr</strong> · WhipCount™<br/><a href="mailto:admin@whipcount.us">admin@whipcount.us</a> · whipcount.us</p>
            </div>
            <div className="privacy-modal-foot">
              <button className="widget-action" onClick={() => setShowTos(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* ── FIRST LOGIN TUTORIAL ── */}
      {showTutorial && (
        <div className="tutorial-overlay" onClick={dismissTutorial}>
          <div className="tutorial-modal" onClick={e=>e.stopPropagation()}>
            <div className="tutorial-head">
              <div className="tutorial-wordmark">Whip<em>Count</em>™</div>
              <div className="tutorial-subtitle">MICHIGAN LEGISLATIVE INTELLIGENCE · GETTING STARTED</div>
            </div>
            <div className="tutorial-steps">
              <div className="tutorial-step" onClick={()=>{setTutorialFrom(true);setShowSearch(true);}}>
                <div className="tutorial-step-num">1</div>
                <div className="tutorial-step-text">
                  <div className="tutorial-step-title">Follow Committees</div>
                  <div className="tutorial-step-sub">Track committees relevant to your clients. Their meetings will appear in your Today View.</div>
                </div>
                <div className="tutorial-step-arrow">→</div>
              </div>
              <div className="tutorial-step" onClick={()=>{dismissTutorial();onNavigate("billtracker");}}>
                <div className="tutorial-step-num">2</div>
                <div className="tutorial-step-text">
                  <div className="tutorial-step-title">Track Bills</div>
                  <div className="tutorial-step-sub">Search the Michigan bill database and add bills to your personal watchlist.</div>
                </div>
                <div className="tutorial-step-arrow">→</div>
              </div>
              <div className="tutorial-step" onClick={()=>{dismissTutorial();onNavigate("clients");}}>
                <div className="tutorial-step-num">3</div>
                <div className="tutorial-step-text">
                  <div className="tutorial-step-title">Add Your First Client</div>
                  <div className="tutorial-step-sub">Log meetings, track legislation, and generate activity reports for your clients.</div>
                </div>
                <div className="tutorial-step-arrow">→</div>
              </div>
            </div>
            <div className="tutorial-foot">
              <button className="tutorial-dismiss" onClick={dismissTutorial}>
                I'll explore on my own →
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmState && <ConfirmDialog message={confirmState.message} subtext={confirmState.subtext} onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)} />}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </>
  );
}
